import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useClient } from '../context/ClientContext';
import ClientWorkspaceLayout from '../components/workspace/ClientWorkspaceLayout';
import { 
  Sunrise, Sun, Moon, Apple, Utensils, Edit2, 
  Trash2, ArrowLeft, Calendar,
  ChevronDown, Plus, Save, Power, PowerOff, AlertCircle, RefreshCw, X,
  Sparkles, CheckCircle, AlertTriangle, GripVertical
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  fetchClientMealPlans,
  fetchMealPlanById,
  updateMealPlan,
  activateMealPlan,
  deactivateMealPlan,
  deleteMealPlan,
} from '../services/clientApi';
import { getNewMealPlanNotifyMessage } from '../constants/profileOptions';
import { translateMenu } from '../services/translateService';
import API_BASE_URL from '../config/api';
import { isDemoUserCode, isDemoMealPlanId } from '../services/tourDemo';
import { toast } from 'sonner';
import AddIngredientModal from '../components/AddIngredientModal';
import IngredientPortionModal from '../components/IngredientPortionModal';
import {
  roundMacro,
  isWithinAiMealMacroMargin,
  getAiMealMacroRange,
  sumIngredientNutrition,
  normalizeAiDish,
  normalizeMealNameForSave,
  formatDishForMealPlanDb,
  countAlternativeDishes,
  normalizeMealAlternativeSlots,
  getAltDishFromMeal,
  writeAltDishToMeal,
  removeAltDishFromMeal,
} from '../utils/mealPlanAlternatives';

// --- Meal slots: each meal time is a fixed slot; the dish inside it can be
// dragged onto another slot, and the two dishes trade places. Defined at module
// level so the hooks keep a stable identity across page re-renders.
function MealSlotDrop({ index, label, dropHint, children }) {
  const { isOver, active, setNodeRef } = useDroppable({ id: `meal-slot-${index}`, data: { index } });
  const isSource = active?.data?.current?.index === index;
  const highlight = isOver && !isSource;
  return (
    <section
      ref={setNodeRef}
      aria-label={label}
      className={`relative rounded-lg transition-colors duration-120 ${
        highlight ? 'ring-2 ring-forest bg-forest-faint dark:bg-forest-faint-dark' : active ? 'ring-1 ring-dashed ring-line dark:ring-line-dark' : ''
      }`}
    >
      {children}
      {highlight ? (
        <span className="pointer-events-none absolute top-2 end-2 inline-flex items-center rounded-sm bg-forest-ondark px-2 py-0.5 text-[11px] font-semibold text-white dark:bg-forest dark:text-canvas-dark">
          {dropHint}
        </span>
      ) : null}
    </section>
  );
}

function MealDragHandle({ index, label, disabled }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `meal-dish-${index}`,
    data: { index },
    disabled,
  });
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`min-h-11 min-w-9 -ms-1 me-1 rounded-md flex items-center justify-center text-ink-muted dark:text-ink-muted-dark hover:bg-moss dark:hover:bg-moss-dark outline-none focus-visible:ring-2 focus-visible:ring-forest touch-none ${
        disabled ? 'opacity-30 cursor-not-allowed' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
    >
      <GripVertical size={18} aria-hidden />
    </button>
  );
}

function ClientMealPlansPage() {
  const navigate = useNavigate();
  const { isDarkMode, themeClasses: baseThemeClasses } = useTheme();
  const { selectedClient } = useClient();

  // DESIGN.md tokens — forest only on View (list) / Save (detail)
  const themeClasses = {
    ...baseThemeClasses,
    inputField: `w-full min-h-11 rounded-md border px-3 py-2 text-[13px] outline-none transition-colors duration-120 focus:border-forest focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark ${isDarkMode ? 'bg-surface-dark border-line-dark text-ink-dark placeholder-ink-muted-dark' : 'bg-surface border-line text-ink placeholder-ink-muted'}`,
    select: `w-full min-h-11 rounded-md border px-3 py-2 text-[13px] outline-none transition-colors duration-120 focus:border-forest focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas-dark appearance-none bg-no-repeat pr-10 ${isDarkMode ? 'bg-surface-dark border-line-dark text-ink-dark [&_option]:bg-surface-dark' : 'bg-surface border-line text-ink'}`,
    btnPrimary: `px-3 py-2.5 min-h-11 rounded-md font-semibold bg-forest-ondark text-white hover:brightness-95 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover dark:hover:brightness-100 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 dark:focus-visible:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-120`,
    btnSecondary: `px-3 py-2.5 min-h-11 rounded-md font-medium border border-line dark:border-line-dark bg-transparent text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 dark:focus-visible:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-120`,
    btnGhost: `px-3 py-2 min-h-11 rounded-md font-medium text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-forest flex items-center justify-center gap-1.5 transition-colors duration-120`,
    btnDanger: `px-3 py-2.5 min-h-11 rounded-md font-medium border border-red-200 dark:border-red-800 bg-transparent text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 dark:focus-visible:ring-offset-canvas-dark flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-120`,
    fieldLabel: `text-[11px] font-medium ${baseThemeClasses.textMuted} block mb-1`,
  };

  const cardBase = `${themeClasses.bgCard} rounded-lg border border-line dark:border-line-dark`;
  const iconBtn = `min-h-11 min-w-11 p-2 rounded-md flex items-center justify-center text-ink-muted dark:text-ink-muted-dark hover:bg-moss dark:hover:bg-moss-dark transition-colors duration-120 outline-none focus-visible:ring-2 focus-visible:ring-forest disabled:opacity-40 disabled:cursor-not-allowed`;
  const overlayClass = 'fixed inset-0 bg-ink/40 dark:bg-canvas-dark/80 flex items-center justify-center z-[9999]';
  const macroText = `text-[13px] font-medium tabular-nums ${themeClasses.textPrimary}`;
  const pillSm = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-medium whitespace-nowrap';

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';
  const isRTL = direction === 'rtl';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  // --- STATE ---
  const [mealPlans, setMealPlans] = useState([]);
  const [selectedMealPlan, setSelectedMealPlan] = useState(null);
  const [, setOriginalMealPlan] = useState(null); 
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [expandedMeals, setExpandedMeals] = useState({});
  const [expandedAlternatives, setExpandedAlternatives] = useState({});
  const [isActivateModalVisible, setIsActivateModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [planToActivate, setPlanToActivate] = useState(null);
  const [isEditScheduleMode, setIsEditScheduleMode] = useState(false); 

  // Activation modal state
  const [activeFrom, setActiveFrom] = useState('');
  const [activeUntil, setActiveUntil] = useState('');
  const [activeDays, setActiveDays] = useState([]);
  const [sendNotifyAfterActivate, setSendNotifyAfterActivate] = useState(false);

  // Ingredient modal states
  const [isAddIngredientModalVisible, setIsAddIngredientModalVisible] = useState(false);
  const [selectedMealIndex, setSelectedMealIndex] = useState(null);
  const [isEditPortionModalVisible, setIsEditPortionModalVisible] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [editingIngredientIndex, setEditingIngredientIndex] = useState(null);
  const [isReplacingIngredient, setIsReplacingIngredient] = useState(false);
  const [replacingIngredientIndex, setReplacingIngredientIndex] = useState(null);

  // Alternative meal editing state
  const [selectedAlternativeMealIndex, setSelectedAlternativeMealIndex] = useState(null);
  const [selectedAlternativeType, setSelectedAlternativeType] = useState(null);
  const [selectedAlternativeIndex, setSelectedAlternativeIndex] = useState(null);
  const [isAddAlternativeIngredientModalVisible, setIsAddAlternativeIngredientModalVisible] = useState(false);
  const [isEditAlternativePortionModalVisible, setIsEditAlternativePortionModalVisible] = useState(false);

  // AI alternative draft state
  const [aiDiscoverPreviews, setAiDiscoverPreviews] = useState({});
  const [aiDiscoverHistory, setAiDiscoverHistory] = useState({});
  const [isDiscoverLoading, setIsDiscoverLoading] = useState({});
  const [isApplyingAiDraft, setIsApplyingAiDraft] = useState({});
  const [selectedAiDraftMealIndex, setSelectedAiDraftMealIndex] = useState(null);
  const [isAddAiDraftIngredientModalVisible, setIsAddAiDraftIngredientModalVisible] = useState(false);
  const [isEditAiDraftPortionModalVisible, setIsEditAiDraftPortionModalVisible] = useState(false);
  const mealAlternativesRefs = useRef({});

  const [editingMealTitle, setEditingMealTitle] = useState(null);

  // --- TRANSLATIONS ---
  const mealTranslations = {
    english: {
      'meal.preMorning': 'Pre-Morning Snack',
      'meal.breakfast': 'Breakfast',
      'meal.morningSnack': 'Morning Snack',
      'meal.lunch': 'Lunch',
      'meal.afternoonSnack': 'Afternoon Snack',
      'meal.preWorkout': 'Pre-Workout Meal',
      'meal.postWorkout': 'Post-Workout Meal',
      'meal.dinner': 'Dinner',
      'meal.eveningSnack': 'Evening Snack',
      'meal.nightSnack': 'Night Snack',
    },
    hebrew: {
      'meal.preMorning': 'נשנוש בוקר מוקדם',
      'meal.breakfast': 'ארוחת בוקר',
      'meal.morningSnack': 'ביניים בוקר',
      'meal.lunch': 'ארוחת צהריים',
      'meal.afternoonSnack': 'ביניים אחר הצהריים',
      'meal.preWorkout': 'לפני אימון',
      'meal.postWorkout': 'אחרי אימון',
      'meal.dinner': 'ארוחת ערב',
      'meal.eveningSnack': 'ביניים ערב',
      'meal.nightSnack': 'ארוחת לילה',
    }
  };

  const translations = {
    english: {
      'page.title': 'Client Meal Plans',
      'page.createPlan': 'Create nutrition plan',
      'page.selectClient': 'Please select a client from the top bar.',
      'page.loading': 'Loading meal plans...',
      'page.noMealPlans': 'No meal plans found for this client.',
      'page.mealPlanName': 'Meal Plan Name',
      'page.mealPlanLabel': 'Meal Plan',
      'page.status': 'Status',
      'page.createdAt': 'Created',
      'page.updatedAt': 'Updated',
      'page.activeFrom': 'Active From',
      'page.activeUntil': 'Active Until',
      'page.activeDays': 'Active Days',
      'page.view': 'View',
      'page.activate': 'Activate',
      'page.deactivate': 'Deactivate',
      'page.edit': 'Edit',
      'page.editSchedule': 'Edit schedule',
      'page.back': 'Back',
      'page.save': 'Save Changes',
      'page.saving': 'Saving...',
      'page.dailySummary': 'Daily Summary',
      'page.protein': 'Protein',
      'page.carbs': 'Carbs',
      'page.fat': 'Fat',
      'page.meals': 'Meals',
      'page.mealsPlanned': 'meals planned',
      'page.ingredients': 'Ingredients',
      'page.items': 'Items',
      'page.addIngredient': 'Add ingredient',
      'page.noIngredients': 'No ingredients available',
      'page.alternativeMeal': 'Alternative Meal',
      'page.alternativeMeals': 'Alternative Meals',
      'page.clickToView': 'Click to view options',
      'page.macroDistribution': 'Macro Distribution',
      'page.status.draft': 'Draft',
      'page.status.scheduled': 'Scheduled',
      'page.status.active': 'Active',
      'page.status.expired': 'Expired',
      'page.currentPlan': 'Current plan',
      'page.currentPlanHint': 'This is the meal plan on the client’s app right now.',
      'page.noCurrentPlan': 'No plan is live on the client’s app',
      'page.noCurrentPlanHint': 'Activate one of the plans below to put it on their phone.',
      'page.morePlans': 'More plans',
      'page.morePlansHint': 'Drafts, scheduled, and previous plans — not what the client sees now.',
      'page.activateModal.title': 'Activate Meal Plan',
      'page.activateModal.activeFrom': 'Active From',
      'page.activateModal.activeUntil': 'Active Until',
      'page.activateModal.activeDays': 'Active Days',
      'page.activateModal.cancel': 'Cancel',
      'page.activateModal.activate': 'Activate',
      'page.activateModal.activating': 'Activating...',
      'page.activateModal.editTitle': 'Edit schedule',
      'page.activateModal.save': 'Save',
      'page.activateModal.saving': 'Saving...',
      'page.activateModal.notifyCheckbox': 'Send WhatsApp: “You have a new meal plan…”',
      'page.activateModal.notifyCheckboxHint': 'Message is sent in the client’s profile language.',
      'page.activateModal.notifyOk': 'WhatsApp notification sent.',
      'page.activateModal.notifyNoPhone': 'WhatsApp not sent: client has no phone number.',
      'page.activateModal.notifyFailed': 'WhatsApp notification could not be sent.',
      'page.days.sunday': 'Sun',
      'page.days.monday': 'Mon',
      'page.days.tuesday': 'Tue',
      'page.days.wednesday': 'Wed',
      'page.days.thursday': 'Thu',
      'page.days.friday': 'Fri',
      'page.days.saturday': 'Sat',
      'page.activateModal.impact.removeCurrent': 'This will remove the current meal plan(s) for the selected period.',
      'page.activateModal.impact.replaceFullWeek': 'This will replace the current meal plan for the whole week.',
      'page.activateModal.impact.disableForDays': 'This will disable the current meal plan for {{days}}.',
      'page.activateModal.impact.disableCurrentAndLeaveBlank': "This will disable the client's current meal plan ({{currentDays}}) and will leave {{blankDays}} blank.",
      'page.activateModal.impact.onlyOnDaysLeaveBlank': 'The new plan will apply only on {{days}}. The other days will be left blank.',
      'page.activateModal.impact.otherPlansUnaffected': 'Other active meal plans will not be affected.',
      'page.activateModal.impact.otherDaysKeepCurrent': 'The other days will keep the current meal plan.',
      'page.deactivateConfirm.base': 'Are you sure you want to deactivate this meal plan?',
      'page.deactivateConfirm.onlyPlan': 'This will deactivate the only meal plan the client has.',
      'page.deactivateConfirm.otherRemains': 'The other meal plan(s) will remain active and will only apply on the days they were set for (not the whole week).',
      'page.activateModal.willCancelCurrent': 'This will cancel the current meal plan(s) for the overlapping days.',
      'page.activateModal.willCancelCurrentPlans': 'Plan(s) that will be cancelled: {{names}}',
      'page.activateModal.onePlanPerWholeWeek': 'Only one meal plan can be active for the whole week.',
      'page.activateModal.willDeactivateCurrent': "Activating this plan will deactivate the client's current meal plan.",
      'page.activateModal.impact.otherDaysNoPlan': 'The other days ({{days}}) will not have any meal plan.',
      'page.activateModal.impact.someDaysNoPlan': 'The following day(s) will not have any meal plan: {{days}}.',
      'page.delete': 'Delete',
      'page.deletePlan': 'Delete meal plan',
      'page.deleteConfirm': 'Are you sure you want to delete this meal plan? This cannot be undone.',
      'page.deleteConfirmActive': 'This meal plan is active. It will be deactivated (removed from the client\'s app) and then deleted. Continue?',
      'page.deletedSuccess': 'Meal plan deleted successfully.',
      'page.deletedError': 'Failed to delete meal plan.',
      'page.mealSlotName': 'Meal name',
      'page.moveBefore': 'Move before',
      'page.moveAfter': 'Move after',
      'page.dragDish': 'Drag to another meal',
      'page.moveTo': 'Move to…',
      'page.moveToLabel': 'Move this dish to another meal',
      'page.dropToSwap': 'Drop to swap',
      'page.emptySlot': 'No meal yet — drag one here',
      'page.discoverAIAlternatives': 'Discover AI Alternative Ideas',
      'page.draftHelper': 'Edit the draft below, then add it to the plan when ready.',
      'page.generateSuggestion': 'Generate',
      'page.generateAnother': 'Generate Another',
      'page.generatingSuggestion': 'Generating…',
      'page.aiSuggestion': 'AI Suggestion',
      'page.aiDraftBadge': 'AI',
      'page.mainProtein': 'Main protein',
      'page.macroDiffFromTarget': 'vs meal target',
      'page.macroDiffOk': 'Within target range',
      'page.macroDiffWarn': 'Outside target range',
      'page.addToPlan': 'Add to plan',
      'page.addToPlanSaving': 'Adding…',
      'page.addToPlanSaveError': 'Failed to add alternative to plan',
      'page.discardDraft': 'Discard draft',
      'page.draftNeedsTitle': 'Please set a dish title before adding to the plan.',
      'page.draftNeedsIngredients': 'Add at least one ingredient before adding to the plan.',
      'page.deleteAlternativeConfirm': 'Remove this alternative meal from the plan?',
      'page.cannotDeleteLastAlternative': 'At least one alternative meal must remain. You cannot delete the last one.',
      'page.deleteAlternative': 'Remove alternative',
    },
    hebrew: {
      'page.title': 'תוכניות ארוחות',
      'page.createPlan': 'בניית תוכנית תזונה',
      'page.selectClient': 'אנא בחר לקוח מהסרגל העליון.',
      'page.loading': 'טוען תוכניות ארוחות...',
      'page.noMealPlans': 'לא נמצאו תוכניות ארוחות עבור לקוח זה.',
      'page.mealPlanName': 'שם תוכנית',
      'page.mealPlanLabel': 'תוכנית ארוחות',
      'page.status': 'סטטוס',
      'page.createdAt': 'נוצר',
      'page.updatedAt': 'עודכן',
      'page.activeFrom': 'פעיל מ',
      'page.activeUntil': 'פעיל עד',
      'page.activeDays': 'ימים פעילים',
      'page.view': 'צפה',
      'page.activate': 'הפעל',
      'page.deactivate': 'בטל',
      'page.edit': 'ערוך',
      'page.editSchedule': 'ערוך תזמון',
      'page.back': 'חזור',
      'page.save': 'שמור',
      'page.saving': 'שומר...',
      'page.dailySummary': 'סיכום יומי',
      'page.protein': 'חלבון',
      'page.carbs': 'פחמימות',
      'page.fat': 'שומן',
      'page.meals': 'ארוחות',
      'page.mealsPlanned': 'ארוחות מתוכננות',
      'page.ingredients': 'מרכיבים',
      'page.items': 'פריטים',
      'page.addIngredient': 'הוסף מרכיב',
      'page.noIngredients': 'אין מרכיבים',
      'page.alternativeMeal': 'ארוחה חלופית',
      'page.alternativeMeals': 'ארוחות חלופיות',
      'page.clickToView': 'לחץ לאפשרויות',
      'page.macroDistribution': 'התפלגות מקרו',
      'page.status.draft': 'טיוטה',
      'page.status.scheduled': 'מתוזמן',
      'page.status.active': 'פעיל',
      'page.status.expired': 'פג תוקף',
      'page.currentPlan': 'התוכנית הנוכחית',
      'page.currentPlanHint': 'זו תוכנית הארוחות באפליקציית הלקוח כרגע.',
      'page.noCurrentPlan': 'אין תוכנית פעילה באפליקציית הלקוח',
      'page.noCurrentPlanHint': 'הפעילו אחת מהתוכניות למטה כדי שתופיע בטלפון שלהם.',
      'page.morePlans': 'תוכניות נוספות',
      'page.morePlansHint': 'טיוטות, מתוזמנות וקודמות — לא מה שהלקוח רואה עכשיו.',
      'page.activateModal.title': 'הפעל תוכנית',
      'page.activateModal.activeFrom': 'פעיל מ',
      'page.activateModal.activeUntil': 'פעיל עד',
      'page.activateModal.activeDays': 'ימים פעילים',
      'page.activateModal.cancel': 'ביטול',
      'page.activateModal.activate': 'הפעל',
      'page.activateModal.activating': 'מפעיל...',
      'page.activateModal.editTitle': 'ערוך תזמון',
      'page.activateModal.save': 'שמור',
      'page.activateModal.saving': 'שומר...',
      'page.activateModal.notifyCheckbox': 'שלח ב-WhatsApp: «יש לך תוכנית ארוחות חדשה…»',
      'page.activateModal.notifyCheckboxHint': 'ההודעה נשלחת בשפת הלקוח (מהפרופיל).',
      'page.activateModal.notifyOk': 'הודעת WhatsApp נשלחה.',
      'page.activateModal.notifyNoPhone': 'WhatsApp לא נשלח: אין מספר טלפון ללקוח.',
      'page.activateModal.notifyFailed': 'שליחת הודעת WhatsApp נכשלה.',
      'page.days.sunday': 'א',
      'page.days.monday': 'ב',
      'page.days.tuesday': 'ג',
      'page.days.wednesday': 'ד',
      'page.days.thursday': 'ה',
      'page.days.friday': 'ו',
      'page.days.saturday': 'ש',
      'page.activateModal.impact.removeCurrent': 'פעולה זו תסיר את תוכנית/ות הארוחות הנוכחיות לתקופה שנבחרה.',
      'page.activateModal.impact.replaceFullWeek': 'פעולה זו תחליף את תוכנית הארוחות הנוכחית לכל השבוע.',
      'page.activateModal.impact.disableForDays': 'פעולה זו תבטל את תוכנית הארוחות הנוכחית בימים: {{days}}.',
      'page.activateModal.impact.disableCurrentAndLeaveBlank': "פעולה זו תבטל את תוכנית הארוחות הנוכחית של הלקוח ({{currentDays}}) ותשאיר את {{blankDays}} ריקים.",
      'page.activateModal.impact.onlyOnDaysLeaveBlank': 'התוכנית החדשה תחול רק בימים: {{days}}. שאר הימים יישארו ריקים.',
      'page.activateModal.impact.otherPlansUnaffected': 'תוכניות ארוחות פעילות אחרות לא יושפעו.',
      'page.activateModal.impact.otherDaysKeepCurrent': 'שאר הימים יישארו עם תוכנית הארוחות הנוכחית.',
      'page.deactivateConfirm.base': 'האם אתה בטוח שברצונך לבטל את הפעלת תוכנית זו?',
      'page.deactivateConfirm.onlyPlan': 'פעולה זו תבטל את תוכנית הארוחות היחידה של הלקוח.',
      'page.deactivateConfirm.otherRemains': 'תוכנית/ות הארוחות האחרות יישארו פעילות ויחולו רק בימים שהוגדרו להן (לא לכל השבוע).',
      'page.activateModal.willCancelCurrent': 'פעולה זו תבטל את תוכנית/ות הארוחות הנוכחיות בימים החופפים.',
      'page.activateModal.willCancelCurrentPlans': 'תוכניות שיבוטלו: {{names}}',
      'page.activateModal.onePlanPerWholeWeek': 'רק תוכנית ארוחות אחת יכולה להיות פעילה לכל השבוע.',
      'page.activateModal.willDeactivateCurrent': 'הפעלת תוכנית זו תבטל את תוכנית הארוחות הנוכחית של הלקוח.',
      'page.activateModal.impact.otherDaysNoPlan': 'בימים האחרים ({{days}}) לא תהיה תוכנית ארוחות.',
      'page.activateModal.impact.someDaysNoPlan': 'בימים הבאים לא תהיה תוכנית ארוחות: {{days}}.',
      'page.delete': 'מחק',
      'page.deletePlan': 'מחק תוכנית ארוחות',
      'page.deleteConfirm': 'האם אתה בטוח שברצונך למחוק תוכנית זו? לא ניתן לשחזר.',
      'page.deleteConfirmActive': 'תוכנית זו פעילה. היא תבוטל (תוסר מאפליקציית הלקוח) ואז תימחק. להמשיך?',
      'page.deletedSuccess': 'תוכנית הארוחות נמחקה בהצלחה.',
      'page.deletedError': 'שגיאה במחיקת תוכנית הארוחות.',
      'page.mealSlotName': 'שם ארוחה',
      'page.moveBefore': 'העבר לפני',
      'page.moveAfter': 'העבר אחרי',
      'page.dragDish': 'גררו לארוחה אחרת',
      'page.moveTo': 'העבר אל…',
      'page.moveToLabel': 'העברת המנה לארוחה אחרת',
      'page.dropToSwap': 'שחררו להחלפה',
      'page.emptySlot': 'אין עדיין מנה — גררו לכאן',
      'page.discoverAIAlternatives': 'גלה רעיונות חלופיים עם AI',
      'page.draftHelper': 'ערוך את הטיוטה למטה, ואז הוסף לתוכנית כשמוכן.',
      'page.generateSuggestion': 'צור הצעה',
      'page.generateAnother': 'צור הצעה נוספת',
      'page.generatingSuggestion': 'יוצר…',
      'page.aiSuggestion': 'הצעת AI',
      'page.aiDraftBadge': 'AI',
      'page.mainProtein': 'חלבון עיקרי',
      'page.macroDiffFromTarget': 'מול יעד הארוחה',
      'page.macroDiffOk': 'בטווח היעד',
      'page.macroDiffWarn': 'מחוץ לטווח היעד',
      'page.addToPlan': 'הוסף לתוכנית',
      'page.addToPlanSaving': 'מוסיף…',
      'page.addToPlanSaveError': 'הוספת החלופה לתוכנית נכשלה',
      'page.discardDraft': 'בטל טיוטה',
      'page.draftNeedsTitle': 'הגדר שם מנה לפני הוספה לתוכנית.',
      'page.draftNeedsIngredients': 'הוסף לפחות מרכיב אחד לפני הוספה לתוכנית.',
      'page.deleteAlternativeConfirm': 'להסיר את מנת החלופה מהתוכנית?',
      'page.cannotDeleteLastAlternative': 'חייבת להישאר לפחות מנת חלופה אחת. לא ניתן למחוק את האחרונה.',
      'page.deleteAlternative': 'הסר חלופה',
    }
  };

  const t = (key) => {
    const trans = translations[language] || translations.english;
    return trans[key] || key;
  };

  const translateMealName = (mealName) => {
    if (!mealName) return mealName;
    let nameKey = null;
    const englishTranslations = mealTranslations.english;
    
    if (mealName.startsWith('meal.')) {
      nameKey = mealName;
    } else {
      for (const [key, value] of Object.entries(englishTranslations)) {
        if (value === mealName) {
          nameKey = key;
          break;
        }
      }
      if (!nameKey) {
        const hebrewTranslations = mealTranslations.hebrew;
        for (const [key, value] of Object.entries(hebrewTranslations)) {
          if (value === mealName) {
            nameKey = key;
            break;
          }
        }
      }
    }
    
    if (nameKey) {
      const currentTranslations = mealTranslations[language] || mealTranslations.english;
      return currentTranslations[nameKey] || mealName;
    }
    return mealName;
  };

  const normalizeMealNameToEnglish = (mealName) => {
    if (!mealName) return mealName;
    const normalizedInput = mealName.toString().trim();
    const lowerInput = normalizedInput.toLowerCase();
    if (normalizedInput.startsWith('meal.')) {
      return mealTranslations.english[normalizedInput] || normalizedInput;
    }
    for (const [key, value] of Object.entries(mealTranslations.english)) {
      if (value === normalizedInput) return value;
      if (key === normalizedInput) return value;
      if (value.toLowerCase() === lowerInput) return value;
      if (key.replace('meal.', '').toLowerCase() === lowerInput) return value;
    }
    for (const [key, value] of Object.entries(mealTranslations.hebrew)) {
      if (value === normalizedInput) return mealTranslations.english[key] || normalizedInput;
    }
    return normalizedInput;
  };

  const mealNameOptions = Object.entries(mealTranslations.english).map(([key, englishLabel]) => ({
    key,
    value: englishLabel,
    label: (mealTranslations[language] && mealTranslations[language][key]) || englishLabel,
  }));

  const dayNames = [
    t('page.days.sunday'),
    t('page.days.monday'),
    t('page.days.tuesday'),
    t('page.days.wednesday'),
    t('page.days.thursday'),
    t('page.days.friday'),
    t('page.days.saturday'),
  ];

  // --- LOAD MEAL PLANS ---
  useEffect(() => {
    if (!selectedClient?.user_code) {
      setMealPlans([]);
      setIsLoading(false);
      return;
    }

    const loadMealPlans = async () => {
      setIsLoading(true);
      try {
        const plans = await fetchClientMealPlans(selectedClient.user_code);
        setMealPlans(plans || []);
      } catch (error) {
        console.error('Error loading meal plans:', error);
        setMealPlans([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadMealPlans();
  }, [selectedClient]);

  const translateAndSetMealPlan = async (plan, targetLanguage) => {
    if (!plan || !plan.meal_plan) return plan;
    setOriginalMealPlan({ ...plan });

    // Tour demo plans are shown as-is: no network call for the example client.
    if (targetLanguage === 'hebrew' && plan.meal_plan && !isDemoMealPlanId(plan.id)) {
      try {
        const menuToTranslate = {
          meals: plan.meal_plan.meals || [],
          note: plan.meal_plan.note || ''
        };
        const translatedMenu = await translateMenu(menuToTranslate, 'he');
        if (translatedMenu && translatedMenu.meals && Array.isArray(translatedMenu.meals)) {
          plan.meal_plan = {
            ...plan.meal_plan,
            meals: translatedMenu.meals,
            note: translatedMenu.note || plan.meal_plan.note || ''
          };
        }
      } catch (translateError) {
        console.error('❌ Translation error (using original):', translateError);
      }
    }
    return plan;
  };

  // --- LOAD SELECTED MEAL PLAN ---
  const handleViewMealPlan = async (mealPlanId) => {
    setIsLoadingPlan(true);
    try {
      const plan = await fetchMealPlanById(mealPlanId);
      if (!plan || !plan.meal_plan) {
        toast.error(language === 'hebrew' ? 'שגיאה: תוכנית הארוחות לא נמצאה' : 'Error: Meal plan not found');
        setIsLoadingPlan(false);
        return;
      }
      const translatedPlan = await translateAndSetMealPlan(plan, language);
      setSelectedMealPlan(translatedPlan);
      
      if (translatedPlan.meal_plan && translatedPlan.meal_plan.meals) {
        const allExpanded = {};
        const allAlternativesExpanded = {};
        translatedPlan.meal_plan.meals.forEach((_, idx) => {
          allExpanded[idx] = false;
          allAlternativesExpanded[idx] = true;
        });
        setExpandedMeals(allExpanded);
        setExpandedAlternatives(allAlternativesExpanded);
      }
    } catch (error) {
      console.error('Error loading meal plan:', error);
      toast.error(language === 'hebrew' ? 'שגיאה בטעינת תוכנית הארוחות' : 'Error loading meal plan');
    } finally {
      setIsLoadingPlan(false);
    }
  };

  useEffect(() => {
    if (!selectedMealPlan?.id) return;
    const reTranslateMealPlan = async () => {
      try {
        setIsLoadingPlan(true);
        const plan = await fetchMealPlanById(selectedMealPlan.id);
        if (!plan || !plan.meal_plan) {
          setIsLoadingPlan(false);
          return;
        }
        const translatedPlan = await translateAndSetMealPlan({ ...plan }, language);
        if (translatedPlan && translatedPlan.meal_plan) {
          setSelectedMealPlan(translatedPlan);
        }
      } catch (error) {
        try {
          const plan = await fetchMealPlanById(selectedMealPlan.id);
          if (plan && plan.meal_plan) setSelectedMealPlan(plan);
        } catch (reloadError) {}
      } finally {
        setIsLoadingPlan(false);
      }
    };
    reTranslateMealPlan();
  }, [language]); 

  // --- ACTIVATION / EDIT SCHEDULE HANDLERS ---
  const handleOpenActivateModal = (plan) => {
    setPlanToActivate(plan);
    setActiveFrom(plan.active_from ? plan.active_from.split('T')[0] : '');
    setActiveUntil(plan.active_until ? plan.active_until.split('T')[0] : '');
    setActiveDays(plan.active_days && plan.active_days.length > 0 ? [...plan.active_days] : []);
    setIsEditScheduleMode(false);
    setSendNotifyAfterActivate(false);
    setIsActivateModalVisible(true);
  };

  const handleOpenEditScheduleModal = (plan) => {
    setPlanToActivate(plan);
    setActiveFrom(plan.active_from ? plan.active_from.split('T')[0] : '');
    setActiveUntil(plan.active_until ? plan.active_until.split('T')[0] : '');
    setActiveDays(plan.active_days && plan.active_days.length > 0 ? [...plan.active_days] : []);
    setIsEditScheduleMode(true);
    setSendNotifyAfterActivate(false);
    setIsActivateModalVisible(true);
  };

  const handleCloseActivateModal = () => {
    setIsActivateModalVisible(false);
    setPlanToActivate(null);
    setIsEditScheduleMode(false);
    setSendNotifyAfterActivate(false);
  };

  const handleToggleDay = (day) => {
    setActiveDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => a - b)
    );
  };

  const normalizeMealPlanWeekDays = (daysFromStateOrPlan) => {
    const d = daysFromStateOrPlan;
    if (!d || !Array.isArray(d) || d.length === 0 || d.length === 7) {
      return [0, 1, 2, 3, 4, 5, 6];
    }
    return d;
  };

  const getActivationImpactMessage = () => {
    if (!planToActivate || !activeFrom || !activeUntil) return null;
    const formatDays = (days) => days.map((d) => dayNames[d]).join(', ');
    const from = new Date(activeFrom + 'T00:00:00').getTime();
    const until = new Date(activeUntil + 'T23:59:59').getTime();
    const dateRangesOverlap = (pFrom, pUntil) => {
      if (!pFrom || !pUntil) return false;
      const pStart = new Date(pFrom).getTime();
      const pEnd = new Date(pUntil).getTime();
      return from <= pEnd && pStart <= until;
    };
    const currentPlans = (mealPlans || []).filter(
      (p) =>
        (p.status === 'active' || p.status === 'scheduled') &&
        p.id !== planToActivate.id &&
        dateRangesOverlap(p.active_from, p.active_until)
    );

    const newIsAllWeek = activeDays.length === 0 || activeDays.length === 7;
    const newDaysSet = new Set(normalizeMealPlanWeekDays(activeDays));

    if (currentPlans.length === 0) {
      if (activeDays.length > 0 && activeDays.length < 7) {
        const otherDays = [0, 1, 2, 3, 4, 5, 6].filter((d) => !newDaysSet.has(d));
        if (otherDays.length === 0) return null;
        return t('page.activateModal.impact.otherDaysNoPlan').replace('{{days}}', formatDays(otherDays));
      }
      return null;
    }

    const isStoredFullWeek = (p) =>
      !p.active_days || p.active_days.length === 0 || p.active_days.length === 7;
    const fullWeekPlan = currentPlans.find((p) => isStoredFullWeek(p));

    if (newIsAllWeek) {
      if (currentPlans.length === 1 && fullWeekPlan) {
        return t('page.activateModal.impact.replaceFullWeek');
      }
      return t('page.activateModal.impact.removeCurrent');
    }

    const newDaysList = [...newDaysSet].sort((a, b) => a - b);
    const newDaysStr = formatDays(newDaysList);

    const currentOverlappingByPlan = currentPlans.map((p) => {
      const pDays = normalizeMealPlanWeekDays(p.active_days);
      const overlap = pDays.filter((d) => newDaysSet.has(d));
      const notCoveredByNew = pDays.filter((d) => !newDaysSet.has(d));
      return { plan: p, overlap, notCoveredByNew, pDays };
    }).filter((x) => x.overlap.length > 0);

    const getUncoveredDays = () => {
      const covered = new Set(newDaysSet);
      currentPlans.forEach((p) => {
        const pDays = normalizeMealPlanWeekDays(p.active_days);
        const overlapsNew = pDays.some((d) => newDaysSet.has(d));
        if (!overlapsNew) pDays.forEach((d) => covered.add(d));
      });
      return [0, 1, 2, 3, 4, 5, 6].filter((d) => !covered.has(d));
    };

    if (currentOverlappingByPlan.length === 0) {
      let msg = t('page.activateModal.impact.onlyOnDaysLeaveBlank').replace('{{days}}', newDaysStr);
      if (fullWeekPlan) {
        msg += ' ' + t('page.activateModal.impact.otherDaysKeepCurrent');
      } else if (currentPlans.length > 0) {
        msg += ' ' + t('page.activateModal.impact.otherPlansUnaffected');
      }
      const uncovered = getUncoveredDays();
      if (uncovered.length > 0) {
        msg += ' ' + t('page.activateModal.impact.someDaysNoPlan').replace('{{days}}', formatDays(uncovered));
      }
      return msg;
    }

    if (currentOverlappingByPlan.length === 1) {
      const { overlap, notCoveredByNew, pDays } = currentOverlappingByPlan[0];
      const overlapStr = formatDays([...overlap].sort((a, b) => a - b));
      const notCoveredStr = formatDays([...notCoveredByNew].sort((a, b) => a - b));
      const currentDaysStr = formatDays([...pDays].sort((a, b) => a - b));

      if (notCoveredByNew.length === 0) {
        let msg = t('page.activateModal.impact.disableForDays').replace('{{days}}', overlapStr) +
          (currentPlans.length > 1 ? ' ' + t('page.activateModal.impact.otherPlansUnaffected') : '');
        const uncovered = getUncoveredDays();
        if (uncovered.length > 0) {
          msg += ' ' + t('page.activateModal.impact.someDaysNoPlan').replace('{{days}}', formatDays(uncovered));
        }
        return msg;
      }
      let msg = t('page.activateModal.impact.disableCurrentAndLeaveBlank')
        .replace('{{currentDays}}', currentDaysStr)
        .replace('{{blankDays}}', notCoveredStr);
      if (pDays.length === 7) {
        msg += ' ' + t('page.activateModal.impact.otherDaysKeepCurrent');
      }
      const uncovered = getUncoveredDays();
      if (uncovered.length > 0) {
        msg += ' ' + t('page.activateModal.impact.someDaysNoPlan').replace('{{days}}', formatDays(uncovered));
      }
      return msg;
    }

    const allOverlapDays = new Set(currentOverlappingByPlan.flatMap((x) => x.overlap));
    const overlapStr = formatDays([...allOverlapDays].sort((a, b) => a - b));
    let msg = t('page.activateModal.impact.disableForDays').replace('{{days}}', overlapStr) +
      ' ' + t('page.activateModal.impact.otherPlansUnaffected');
    const uncovered = getUncoveredDays();
    if (uncovered.length > 0) {
      msg += ' ' + t('page.activateModal.impact.someDaysNoPlan').replace('{{days}}', formatDays(uncovered));
    }
    return msg;
  };

  const activationImpactMessage = getActivationImpactMessage();

  const getConflictingPlansForActivation = () => {
    if (!planToActivate || !activeFrom || !activeUntil) return [];
    const from = new Date(activeFrom + 'T00:00:00').getTime();
    const until = new Date(activeUntil + 'T23:59:59').getTime();
    const dateRangesOverlap = (pFrom, pUntil) => {
      if (!pFrom || !pUntil) return false;
      const pStart = new Date(pFrom).getTime();
      const pEnd = new Date(pUntil).getTime();
      return from <= pEnd && pStart <= until;
    };
    const newDaysSet = new Set(normalizeMealPlanWeekDays(activeDays));
    const currentPlans = (mealPlans || []).filter(
      (p) =>
        (p.status === 'active' || p.status === 'scheduled') &&
        p.id !== planToActivate.id &&
        dateRangesOverlap(p.active_from, p.active_until)
    );
    return currentPlans.filter((p) => {
      const pDays = normalizeMealPlanWeekDays(p.active_days);
      return pDays.some((d) => newDaysSet.has(d));
    });
  };

  const conflictingPlansForActivation = getConflictingPlansForActivation();

  const handleActivate = async () => {
    if (!planToActivate?.id) return;
    setIsSaving(true);
    try {
      for (const plan of conflictingPlansForActivation) {
        await deactivateMealPlan(plan.id);
      }
      const activeFromISO = activeFrom ? new Date(activeFrom + 'T00:00:00').toISOString() : null;
      const activeUntilISO = activeUntil ? new Date(activeUntil + 'T23:59:59').toISOString() : null;
      const activeDaysToSend = activeDays.length > 0 ? activeDays : null;
      await activateMealPlan(planToActivate.id, activeFromISO, activeUntilISO, activeDaysToSend);
      const plans = await fetchClientMealPlans(selectedClient.user_code);
      setMealPlans(plans || []);
      if (selectedMealPlan?.id === planToActivate.id) {
        const updatedPlan = await fetchMealPlanById(planToActivate.id);
        setSelectedMealPlan(updatedPlan);
      }
      let notifyLine = '';
      if (sendNotifyAfterActivate && !isEditScheduleMode && selectedClient) {
        const phone = selectedClient.phone_number;
        if (!phone) {
          notifyLine = `\n\n${t('page.activateModal.notifyNoPhone')}`;
        } else {
          const message = getNewMealPlanNotifyMessage(selectedClient.language);
          try {
            const notifyRes = await fetch(
              'https://api-gw.eu-prod.betterchoice.one/whapi/send-external-message',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone_number: phone, message }),
              }
            );
            notifyLine = notifyRes.ok ? `\n\n${t('page.activateModal.notifyOk')}` : `\n\n${t('page.activateModal.notifyFailed')}`;
          } catch (e) {
            notifyLine = `\n\n${t('page.activateModal.notifyFailed')}`;
          }
        }
      }
      handleCloseActivateModal();
      const base = isEditScheduleMode
          ? (language === 'hebrew' ? 'תזמון תוכנית הארוחות עודכן בהצלחה' : 'Meal plan schedule updated successfully')
          : (language === 'hebrew' ? 'תוכנית הארוחות הופעלה בהצלחה' : 'Meal plan activated successfully');
      toast.success(base + notifyLine);
    } catch (error) {
      toast.error(language === 'hebrew' ? 'שגיאה בהפעלת תוכנית הארוחות' : 'Error activating meal plan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async (plan) => {
    if (!plan?.id) return;
    setIsSaving(true);
    try {
      await deactivateMealPlan(plan.id);
      const plans = await fetchClientMealPlans(selectedClient.user_code);
      setMealPlans(plans || []);
      if (selectedMealPlan?.id === plan.id) {
        const updatedPlan = await fetchMealPlanById(plan.id);
        setSelectedMealPlan(updatedPlan);
      }
      toast.success(language === 'hebrew' ? 'תוכנית הארוחות בוטלה בהצלחה' : 'Meal plan deactivated successfully');
    } catch (error) {
      toast.error(language === 'hebrew' ? 'שגיאה בביטול הפעלת תוכנית הארוחות' : 'Error deactivating meal plan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!plan?.id) return;
    setIsSaving(true);
    try {
      await deleteMealPlan(plan.id);
      const plans = await fetchClientMealPlans(selectedClient.user_code);
      setMealPlans(plans || []);
      if (selectedMealPlan?.id === plan.id) setSelectedMealPlan(null);
      toast.success(t('page.deletedSuccess'));
    } catch (error) {
      toast.error(t('page.deletedError'));
    } finally {
      setIsSaving(false);
    }
  };

  // --- INGREDIENT HANDLERS ---
  const handleOpenAddIngredient = (mealIndex) => {
    setSelectedMealIndex(mealIndex);
    setIsReplacingIngredient(false);
    setReplacingIngredientIndex(null);
    setIsAddIngredientModalVisible(true);
  };

  const handleReplaceIngredient = (mealIndex, ingredientIndex) => {
    setSelectedMealIndex(mealIndex);
    setIsReplacingIngredient(true);
    setReplacingIngredientIndex(ingredientIndex);
    setIsAddIngredientModalVisible(true);
  };

  const handleAddIngredient = async (ingredient) => {
    if (selectedMealIndex === null || !selectedMealPlan) return;
    try {
      const updatedPlanData = { ...selectedMealPlan };
      const updatedMeals = [...updatedPlanData.meal_plan.meals];
      const mealToUpdate = { ...updatedMeals[selectedMealIndex] };
      
      if (!mealToUpdate.main) mealToUpdate.main = {};
      if (!mealToUpdate.main.ingredients) mealToUpdate.main.ingredients = [];

      const replacing = isReplacingIngredient && replacingIngredientIndex !== null;
      if (replacing) {
        mealToUpdate.main.ingredients[replacingIngredientIndex] = ingredient;
        mealToUpdate.main.nutrition = mealToUpdate.main.ingredients.reduce(
          (acc, ing) => ({
            calories: acc.calories + (ing.calories || 0),
            protein: acc.protein + (ing.protein || 0),
            carbs: acc.carbs + (ing.carbs || 0),
            fat: acc.fat + (ing.fat || 0),
          }),
          { calories: 0, protein: 0, carbs: 0, fat: 0 }
        );
        setIsReplacingIngredient(false);
        setReplacingIngredientIndex(null);
      } else {
        mealToUpdate.main.ingredients = [...mealToUpdate.main.ingredients, ingredient];
        const currentNutrition = mealToUpdate.main.nutrition || {};
        mealToUpdate.main.nutrition = {
          calories: (currentNutrition.calories || 0) + ingredient.calories,
          protein: (currentNutrition.protein || 0) + ingredient.protein,
          carbs: (currentNutrition.carbs || 0) + ingredient.carbs,
          fat: (currentNutrition.fat || 0) + ingredient.fat,
        };
      }

      updatedMeals[selectedMealIndex] = mealToUpdate;
      updatedPlanData.meal_plan.meals = updatedMeals;

      const newTotals = updatedMeals.reduce(
        (acc, meal) => {
          if (meal.main && meal.main.nutrition) {
            acc.calories += meal.main.nutrition.calories || 0;
            acc.protein += meal.main.nutrition.protein || 0;
            acc.carbs += meal.main.nutrition.carbs || 0;
            acc.fat += meal.main.nutrition.fat || 0;
          }
          return acc;
        },
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedPlanData.meal_plan.totals = newTotals;
      setSelectedMealPlan(updatedPlanData);
      toast.success(language === 'hebrew' ? (replacing ? 'המרכיב הוחלף בהצלחה' : 'המרכיב נוסף בהצלחה') : (replacing ? 'Ingredient replaced successfully' : 'Ingredient added successfully'));
    } catch (error) {
      toast.error(language === 'hebrew' ? 'לא ניתן להוסיף/להחליף את המרכיב. נסה שוב.' : 'Failed to add/replace ingredient. Please try again.');
    }
  };

  const handleEditIngredient = (mealIndex, ingredientIndex, ingredient) => {
    setSelectedMealIndex(mealIndex);
    setEditingIngredientIndex(ingredientIndex);
    setEditingIngredient({
      ...ingredient,
      displayName: ingredient.item || ingredient.name || 'Unknown item',
      calories: ingredient.calories || 0,
      protein: ingredient.protein || 0,
      carbs: ingredient.carbs || 0,
      fat: ingredient.fat || 0,
    });
    setIsEditPortionModalVisible(true);
  };

  const handleUpdateIngredientPortion = async ({ quantity: quantityNum, householdMeasure }) => {
    if (selectedMealIndex === null || editingIngredientIndex === null || !selectedMealPlan || !editingIngredient) return;
    try {
      const updatedPlanData = { ...selectedMealPlan };
      const updatedMeals = [...updatedPlanData.meal_plan.meals];
      const mealToUpdate = { ...updatedMeals[selectedMealIndex] };

      const oldIngredient = mealToUpdate.main.ingredients[editingIngredientIndex];
      const originalScale = (oldIngredient['portionSI(gram)'] || 100) / 100;
      const original100gCalories = oldIngredient.calories / originalScale;
      const original100gProtein = oldIngredient.protein / originalScale;
      const original100gCarbs = oldIngredient.carbs / originalScale;
      const original100gFat = oldIngredient.fat / originalScale;

      const newScale = quantityNum / 100;
      const updatedIngredient = {
        UPC: oldIngredient.UPC || null,
        item: oldIngredient.item,
        'brand of pruduct': oldIngredient['brand of pruduct'] || '',
        household_measure: householdMeasure,
        'portionSI(gram)': quantityNum,
        calories: Math.round(original100gCalories * newScale),
        protein: Math.round(original100gProtein * newScale * 10) / 10,
        carbs: Math.round(original100gCarbs * newScale * 10) / 10,
        fat: Math.round(original100gFat * newScale * 10) / 10,
      };

      mealToUpdate.main.ingredients[editingIngredientIndex] = updatedIngredient;
      mealToUpdate.main.nutrition = mealToUpdate.main.ingredients.reduce(
        (acc, ing) => ({
          calories: acc.calories + (ing.calories || 0),
          protein: acc.protein + (ing.protein || 0),
          carbs: acc.carbs + (ing.carbs || 0),
          fat: acc.fat + (ing.fat || 0),
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedMeals[selectedMealIndex] = mealToUpdate;
      updatedPlanData.meal_plan.meals = updatedMeals;

      const newTotals = updatedMeals.reduce(
        (acc, meal) => {
          if (meal.main && meal.main.nutrition) {
            acc.calories += meal.main.nutrition.calories || 0;
            acc.protein += meal.main.nutrition.protein || 0;
            acc.carbs += meal.main.nutrition.carbs || 0;
            acc.fat += meal.main.nutrition.fat || 0;
          }
          return acc;
        },
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedPlanData.meal_plan.totals = newTotals;
      setSelectedMealPlan(updatedPlanData);
      setIsEditPortionModalVisible(false);
      setEditingIngredient(null);
      setEditingIngredientIndex(null);
      toast.success(language === 'hebrew' ? 'המרכיב עודכן בהצלחה' : 'Ingredient updated successfully');
    } catch (error) {
      toast.error(language === 'hebrew' ? 'לא ניתן לעדכן את המרכיב. נסה שוב.' : 'Failed to update ingredient. Please try again.');
    }
  };

  const handleDeleteIngredient = async (mealIndex, ingredientIndex) => {
    if (!selectedMealPlan) return;
    try {
      const updatedPlanData = { ...selectedMealPlan };
      const updatedMeals = [...updatedPlanData.meal_plan.meals];
      const mealToUpdate = { ...updatedMeals[mealIndex] };

      mealToUpdate.main.ingredients.splice(ingredientIndex, 1);
      mealToUpdate.main.nutrition = mealToUpdate.main.ingredients.reduce(
        (acc, ing) => ({
          calories: acc.calories + (ing.calories || 0),
          protein: acc.protein + (ing.protein || 0),
          carbs: acc.carbs + (ing.carbs || 0),
          fat: acc.fat + (ing.fat || 0),
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedMeals[mealIndex] = mealToUpdate;
      updatedPlanData.meal_plan.meals = updatedMeals;

      const newTotals = updatedMeals.reduce(
        (acc, meal) => {
          if (meal.main && meal.main.nutrition) {
            acc.calories += meal.main.nutrition.calories || 0;
            acc.protein += meal.main.nutrition.protein || 0;
            acc.carbs += meal.main.nutrition.carbs || 0;
            acc.fat += meal.main.nutrition.fat || 0;
          }
          return acc;
        },
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      updatedPlanData.meal_plan.totals = newTotals;
      setSelectedMealPlan(updatedPlanData);
    } catch (error) {
      toast.error(language === 'hebrew' ? 'לא ניתן למחוק את המרכיב. נסה שוב.' : 'Failed to delete ingredient. Please try again.');
    }
  };

  // --- ALTERNATIVE MEAL INGREDIENT HANDLERS ---
  const handleDeleteAlternativeDish = (mealIndex, type = 'alternative', altIndex = null) => {
    if (!selectedMealPlan?.meal_plan?.meals) return;
    const meal = selectedMealPlan.meal_plan.meals[mealIndex];
    if (countAlternativeDishes(meal) <= 1) {
      toast.error(t('page.cannotDeleteLastAlternative'));
      return;
    }

    const updatedPlanData = { ...selectedMealPlan };
    const updatedMeals = [...updatedPlanData.meal_plan.meals];
    updatedMeals[mealIndex] = normalizeMealAlternativeSlots(
      removeAltDishFromMeal({ ...updatedMeals[mealIndex] }, type, altIndex)
    );
    updatedPlanData.meal_plan = { ...updatedPlanData.meal_plan, meals: updatedMeals };
    setSelectedMealPlan(updatedPlanData);
  };

  const handleOpenAddAlternativeIngredient = (mealIndex, type = 'alternative', altIndex = null) => {
    setSelectedAlternativeMealIndex(mealIndex);
    setSelectedAlternativeType(type);
    setSelectedAlternativeIndex(altIndex);
    setIsReplacingIngredient(false);
    setReplacingIngredientIndex(null);
    setIsAddAlternativeIngredientModalVisible(true);
  };

  const handleAddAlternativeIngredient = async (ingredient) => {
    if (selectedAlternativeMealIndex === null || !selectedMealPlan) return;
    const type = selectedAlternativeType || 'alternative';
    const altIndex = selectedAlternativeIndex;
    try {
      const updatedPlanData = { ...selectedMealPlan };
      const updatedMeals = [...updatedPlanData.meal_plan.meals];
      let mealToUpdate = { ...updatedMeals[selectedAlternativeMealIndex] };

      let dish = getAltDishFromMeal(mealToUpdate, type, altIndex);
      if (!dish) {
        dish = { ingredients: [], nutrition: { calories: 0, protein: 0, carbs: 0, fat: 0 } };
      } else {
        dish = { ...dish };
      }
      if (!dish.ingredients) dish.ingredients = [];

      const replacing = isReplacingIngredient && replacingIngredientIndex !== null;
      if (replacing) {
        dish.ingredients[replacingIngredientIndex] = ingredient;
      } else {
        dish.ingredients = [...dish.ingredients, ingredient];
      }
      dish.nutrition = sumIngredientNutrition(dish.ingredients);

      mealToUpdate = writeAltDishToMeal(mealToUpdate, type, altIndex, dish);
      updatedMeals[selectedAlternativeMealIndex] = mealToUpdate;
      updatedPlanData.meal_plan = { ...updatedPlanData.meal_plan, meals: updatedMeals };
      setSelectedMealPlan(updatedPlanData);
      setIsReplacingIngredient(false);
      setReplacingIngredientIndex(null);
      setSelectedAlternativeMealIndex(null);
      setSelectedAlternativeType(null);
      setSelectedAlternativeIndex(null);
      toast.success(language === 'hebrew' ? (replacing ? 'המרכיב הוחלף בהצלחה' : 'המרכיב נוסף בהצלחה') : (replacing ? 'Ingredient replaced successfully' : 'Ingredient added successfully'));
    } catch (error) {
      toast.error(language === 'hebrew' ? 'לא ניתן להוסיף/להחליף את המרכיב. נסה שוב.' : 'Failed to add/replace ingredient. Please try again.');
    }
  };

  const handleEditAlternativeIngredient = (mealIndex, ingredientIndex, ingredient, type = 'alternative', altIndex = null) => {
    setSelectedAlternativeMealIndex(mealIndex);
    setSelectedAlternativeType(type);
    setSelectedAlternativeIndex(altIndex);
    setEditingIngredientIndex(ingredientIndex);
    setEditingIngredient({
      ...ingredient,
      displayName: ingredient.item || ingredient.name || 'Unknown item',
      calories: ingredient.calories || 0,
      protein: ingredient.protein || 0,
      carbs: ingredient.carbs || 0,
      fat: ingredient.fat || 0,
    });
    setIsEditAlternativePortionModalVisible(true);
  };

  const handleUpdateAlternativeIngredientPortion = async ({ quantity: quantityNum, householdMeasure }) => {
    if (selectedAlternativeMealIndex === null || editingIngredientIndex === null || !selectedMealPlan || !editingIngredient) return;
    const type = selectedAlternativeType || 'alternative';
    const altIndex = selectedAlternativeIndex;
    try {
      const updatedPlanData = { ...selectedMealPlan };
      const updatedMeals = [...updatedPlanData.meal_plan.meals];
      let mealToUpdate = { ...updatedMeals[selectedAlternativeMealIndex] };
      let dish = { ...getAltDishFromMeal(mealToUpdate, type, altIndex) };
      const oldIngredient = dish.ingredients[editingIngredientIndex];
      const originalScale = (oldIngredient['portionSI(gram)'] || 100) / 100;
      const original100gCalories = oldIngredient.calories / originalScale;
      const original100gProtein = oldIngredient.protein / originalScale;
      const original100gCarbs = oldIngredient.carbs / originalScale;
      const original100gFat = oldIngredient.fat / originalScale;
      const newScale = quantityNum / 100;
      dish.ingredients = [...dish.ingredients];
      dish.ingredients[editingIngredientIndex] = {
        ...oldIngredient,
        household_measure: householdMeasure,
        'portionSI(gram)': quantityNum,
        calories: Math.round(original100gCalories * newScale),
        protein: Math.round(original100gProtein * newScale * 10) / 10,
        carbs: Math.round(original100gCarbs * newScale * 10) / 10,
        fat: Math.round(original100gFat * newScale * 10) / 10,
      };
      dish.nutrition = sumIngredientNutrition(dish.ingredients);
      mealToUpdate = writeAltDishToMeal(mealToUpdate, type, altIndex, dish);
      updatedMeals[selectedAlternativeMealIndex] = mealToUpdate;
      updatedPlanData.meal_plan = { ...updatedPlanData.meal_plan, meals: updatedMeals };
      setSelectedMealPlan(updatedPlanData);
      setIsEditAlternativePortionModalVisible(false);
      setEditingIngredient(null);
      setEditingIngredientIndex(null);
      setSelectedAlternativeMealIndex(null);
      setSelectedAlternativeType(null);
      setSelectedAlternativeIndex(null);
      toast.success(language === 'hebrew' ? 'המרכיב עודכן בהצלחה' : 'Ingredient updated successfully');
    } catch (error) {
      toast.error(language === 'hebrew' ? 'לא ניתן לעדכן את המרכיב. נסה שוב.' : 'Failed to update ingredient. Please try again.');
    }
  };

  const handleDeleteAlternativeIngredient = async (mealIndex, ingredientIndex, type = 'alternative', altIndex = null) => {
    if (!selectedMealPlan) return;
    try {
      const updatedPlanData = { ...selectedMealPlan };
      const updatedMeals = [...updatedPlanData.meal_plan.meals];
      let mealToUpdate = { ...updatedMeals[mealIndex] };
      let dish = { ...getAltDishFromMeal(mealToUpdate, type, altIndex) };
      dish.ingredients = [...dish.ingredients];
      dish.ingredients.splice(ingredientIndex, 1);
      dish.nutrition = sumIngredientNutrition(dish.ingredients);
      mealToUpdate = writeAltDishToMeal(mealToUpdate, type, altIndex, dish);
      updatedMeals[mealIndex] = mealToUpdate;
      updatedPlanData.meal_plan = { ...updatedPlanData.meal_plan, meals: updatedMeals };
      setSelectedMealPlan(updatedPlanData);
    } catch (error) {
      toast.error(language === 'hebrew' ? 'לא ניתן למחוק את המרכיב. נסה שוב.' : 'Failed to delete ingredient. Please try again.');
    }
  };

  const handleReplaceAlternativeIngredient = (mealIndex, ingredientIndex, type = 'alternative', altIndex = null) => {
    setSelectedAlternativeMealIndex(mealIndex);
    setSelectedAlternativeType(type);
    setSelectedAlternativeIndex(altIndex);
    setIsReplacingIngredient(true);
    setReplacingIngredientIndex(ingredientIndex);
    setIsAddAlternativeIngredientModalVisible(true);
  };

  // --- AI ALTERNATIVE HANDLERS ---
  const handleDiscoverAlternative = async (mealIndex, meal) => {
    const userCode = selectedClient?.user_code || selectedClient?.id;
    if (!userCode) return;

    if (isDemoUserCode(userCode)) {
      const preview = meal.alternatives?.[0] || meal.alternative;
      if (preview) {
        setAiDiscoverPreviews((prev) => ({ ...prev, [mealIndex]: preview }));
      }
      return;
    }

    const nutrition = meal.main?.nutrition || meal.main || {};
    const mealCalories = nutrition.calories || 0;
    const mealProtein = nutrition.protein || 0;
    const mealCarbs = nutrition.carbs || 0;
    const mealFat = nutrition.fat || 0;

    const getMealTitle = (item) =>
      item?.meal_title || item?.meal_name || item?.title || item?.meal || '';

    const mealToExcludeDetail = (item) => {
      if (!item) return null;
      const title = getMealTitle(item);
      const mainProtein = item.main_protein_source || '';
      const ingredients = (item.ingredients || [])
        .map((i) => i?.item || i?.name || '')
        .map((s) => String(s).trim())
        .filter(Boolean);
      if (!title && !mainProtein && ingredients.length === 0) return null;
      return { title, main_protein_source: mainProtein, ingredients };
    };

    const buildExcludeMealDetails = () => {
      const details = [];
      const push = (detail) => { if (detail) details.push(detail); };
      push(mealToExcludeDetail(meal.main));
      if (meal.alternative) push(mealToExcludeDetail(meal.alternative));
      (meal.alternatives || []).forEach((alt) => push(mealToExcludeDetail(alt)));
      (aiDiscoverHistory[mealIndex] || []).forEach((detail) => push(detail));
      push(mealToExcludeDetail(aiDiscoverPreviews[mealIndex]));
      const seen = new Set();
      return details.filter((d) => {
        const key = `${d.title}|${d.main_protein_source}|${d.ingredients.join(',')}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    setIsDiscoverLoading((prev) => ({ ...prev, [mealIndex]: true }));
    try {
      const excludeMealDetails = buildExcludeMealDetails();
      const excludeMeals = excludeMealDetails.map((d) => d.title).filter(Boolean);

      const response = await fetch(`${API_BASE_URL}/meal-plans/generate-alternative`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userCode,
          mealType: meal.meal,
          calories: mealCalories,
          protein: mealProtein,
          carbs: mealCarbs,
          fat: mealFat,
          excludeMeals,
          excludeMealDetails,
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.error || `Request failed: ${response.status}`);
      }

      if (result?.data) {
        const normalized = normalizeAiDish(result.data, meal.meal);
        const detail = {
          title: normalized.meal_title || normalized.meal_name || '',
          main_protein_source: normalized.main_protein_source || '',
          ingredients: (normalized.ingredients || [])
            .map((i) => i?.item || i?.name || '')
            .map((s) => String(s).trim())
            .filter(Boolean),
        };
        setAiDiscoverPreviews((prev) => ({ ...prev, [mealIndex]: normalized }));
        if (detail.title || detail.main_protein_source || detail.ingredients.length) {
          setAiDiscoverHistory((prev) => ({
            ...prev,
            [mealIndex]: [...(prev[mealIndex] || []), detail],
          }));
        }
      }
    } catch (error) {
      console.error('Error generating AI alternative:', error);
      toast.error(
        language === 'hebrew'
          ? 'לא ניתן ליצור הצעה כרגע. נסה שוב.'
          : 'Could not generate a suggestion right now. Please try again.'
      );
    } finally {
      setIsDiscoverLoading((prev) => ({ ...prev, [mealIndex]: false }));
    }
  };

  const updateAiDraft = (mealIndex, updater) => {
    setAiDiscoverPreviews((prev) => {
      const current = prev[mealIndex];
      if (!current) return prev;
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [mealIndex]: next };
    });
  };

  const getMacroDiffChip = (draftNutrition, targetNutrition) => {
    if (!draftNutrition || !targetNutrition) return null;
    const isHe = language === 'hebrew';

    const metrics = [
      { key: 'calories', label: isHe ? 'קלוריות' : 'Calories', unit: 'kcal', round: Math.round },
      { key: 'protein', label: isHe ? 'חלבון' : 'Protein', unit: 'g', round: roundMacro },
      { key: 'carbs', label: isHe ? 'פחמימות' : 'Carbs', unit: 'g', round: roundMacro },
      { key: 'fat', label: isHe ? 'שומן' : 'Fat', unit: 'g', round: roundMacro },
    ];

    const outOfRange = [];
    const detailLines = [];
    let withinTarget = true;

    metrics.forEach((m) => {
      const actual = Number(draftNutrition[m.key]) || 0;
      const target = Number(targetNutrition[m.key]) || 0;
      const range = getAiMealMacroRange(target);
      const displayActual = m.round(actual);
      const displayTarget = m.round(target);
      const inRange = isWithinAiMealMacroMargin(actual, target);
      if (!inRange) withinTarget = false;

      const suffix = m.unit === 'kcal' ? ' kcal' : 'g';
      detailLines.push(
        isHe
          ? `${m.label}: ${displayActual}${suffix} (יעד ${displayTarget}, טווח ${range.min}–${range.max})`
          : `${m.label}: ${displayActual}${suffix} (target ${displayTarget}, range ${range.min}–${range.max})`
      );

      if (!inRange) {
        const diff = actual - target;
        const over = diff > 0;
        outOfRange.push({
          key: m.key,
          line: isHe
            ? `${m.label}: ${displayActual}${suffix} — ${over ? 'גבוה ב' : 'נמוך ב'} ${m.round(Math.abs(diff))}${suffix} (יעד ${displayTarget}, טווח ${range.min}–${range.max})`
            : `${m.label}: ${displayActual}${suffix} — ${over ? 'over' : 'under'} by ${m.round(Math.abs(diff))}${suffix} (target ${displayTarget}, range ${range.min}–${range.max})`,
        });
      }
    });

    return {
      withinTarget,
      label: withinTarget ? t('page.macroDiffOk') : t('page.macroDiffWarn'),
      detail: detailLines.join('\n'),
      outOfRange,
    };
  };

  const handleDiscardAiDraft = (mealIndex) => {
    setAiDiscoverPreviews((prev) => {
      const next = { ...prev };
      delete next[mealIndex];
      return next;
    });
    setEditingMealTitle((prev) =>
      prev?.mealIndex === mealIndex && prev?.type === 'aiDraft' ? null : prev
    );
  };

  const handleApplyAiDraftToPlan = (mealIndex) => {
    const draft = aiDiscoverPreviews[mealIndex];
    if (!draft || !selectedMealPlan?.meal_plan?.meals) return;

    const title = (draft.meal_title || draft.meal_name || '').trim();
    if (!title) {
      toast.error(t('page.draftNeedsTitle'));
      return;
    }
    if (!draft.ingredients?.length) {
      toast.error(t('page.draftNeedsIngredients'));
      return;
    }

    setIsApplyingAiDraft((prev) => ({ ...prev, [mealIndex]: true }));
    try {
      const updatedPlanData = { ...selectedMealPlan };
      const updatedMeals = [...updatedPlanData.meal_plan.meals];
      const mealToUpdate = { ...updatedMeals[mealIndex] };
      const englishMealName = normalizeMealNameForSave(
        mealToUpdate.meal,
        mealTranslations.english,
        mealTranslations.hebrew
      );
      const dishToAdd = formatDishForMealPlanDb(
        {
          ...draft,
          meal_title: title,
          nutrition: sumIngredientNutrition(draft.ingredients),
        },
        englishMealName
      );

      if (mealToUpdate.alternative) {
        mealToUpdate.alternatives = [...(mealToUpdate.alternatives || []), dishToAdd];
      } else {
        mealToUpdate.alternative = dishToAdd;
      }
      updatedMeals[mealIndex] = normalizeMealAlternativeSlots(mealToUpdate);
      updatedPlanData.meal_plan = { ...updatedPlanData.meal_plan, meals: updatedMeals };
      setSelectedMealPlan(updatedPlanData);
      handleDiscardAiDraft(mealIndex);
      setExpandedAlternatives((prev) => ({ ...prev, [mealIndex]: true }));
      setTimeout(() => {
        mealAlternativesRefs.current[mealIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } catch (error) {
      console.error('Error applying AI alternative:', error);
      toast.error(`${t('page.addToPlanSaveError')}: ${error.message}`);
    } finally {
      setIsApplyingAiDraft((prev) => ({ ...prev, [mealIndex]: false }));
    }
  };

  const handleAiDraftTitleChange = (mealIndex, newTitle) => {
    updateAiDraft(mealIndex, (draft) => ({
      ...draft,
      meal_title: newTitle || draft.meal_title || draft.meal_name || '',
    }));
    setEditingMealTitle(null);
  };

  const handleOpenAddAiDraftIngredient = (mealIndex) => {
    setSelectedAiDraftMealIndex(mealIndex);
    setIsReplacingIngredient(false);
    setReplacingIngredientIndex(null);
    setIsAddAiDraftIngredientModalVisible(true);
  };

  const handleReplaceAiDraftIngredient = (mealIndex, ingredientIndex) => {
    setSelectedAiDraftMealIndex(mealIndex);
    setIsReplacingIngredient(true);
    setReplacingIngredientIndex(ingredientIndex);
    setIsAddAiDraftIngredientModalVisible(true);
  };

  const handleAddAiDraftIngredient = (ingredient) => {
    if (selectedAiDraftMealIndex === null) return;
    const mealIndex = selectedAiDraftMealIndex;
    updateAiDraft(mealIndex, (draft) => {
      const ingredients = [...(draft.ingredients || [])];
      const replacing = isReplacingIngredient && replacingIngredientIndex !== null;
      if (replacing) {
        ingredients[replacingIngredientIndex] = ingredient;
      } else {
        ingredients.push(ingredient);
      }
      return {
        ...draft,
        ingredients,
        nutrition: sumIngredientNutrition(ingredients),
      };
    });
    setIsReplacingIngredient(false);
    setReplacingIngredientIndex(null);
    setIsAddAiDraftIngredientModalVisible(false);
    setSelectedAiDraftMealIndex(null);
  };

  const handleEditAiDraftIngredient = (mealIndex, ingredientIndex, ingredient) => {
    setSelectedAiDraftMealIndex(mealIndex);
    setEditingIngredientIndex(ingredientIndex);
    setEditingIngredient({
      ...ingredient,
      displayName: ingredient.item || ingredient.name || 'Unknown item',
      calories: ingredient.calories || 0,
      protein: ingredient.protein || 0,
      carbs: ingredient.carbs || 0,
      fat: ingredient.fat || 0,
    });
    setIsEditAiDraftPortionModalVisible(true);
  };

  const handleUpdateAiDraftIngredientPortion = ({ quantity: quantityNum, householdMeasure }) => {
    if (selectedAiDraftMealIndex === null || editingIngredientIndex === null || !editingIngredient) return;
    const mealIndex = selectedAiDraftMealIndex;
    updateAiDraft(mealIndex, (draft) => {
      const ingredients = [...(draft.ingredients || [])];
      const oldIngredient = ingredients[editingIngredientIndex];
      const originalScale = (oldIngredient['portionSI(gram)'] || 100) / 100;
      const original100gCalories = oldIngredient.calories / originalScale;
      const original100gProtein = oldIngredient.protein / originalScale;
      const original100gCarbs = oldIngredient.carbs / originalScale;
      const original100gFat = oldIngredient.fat / originalScale;
      const newScale = quantityNum / 100;
      ingredients[editingIngredientIndex] = {
        ...oldIngredient,
        household_measure: householdMeasure,
        'portionSI(gram)': quantityNum,
        calories: Math.round(original100gCalories * newScale),
        protein: Math.round(original100gProtein * newScale * 10) / 10,
        carbs: Math.round(original100gCarbs * newScale * 10) / 10,
        fat: Math.round(original100gFat * newScale * 10) / 10,
      };
      return { ...draft, ingredients, nutrition: sumIngredientNutrition(ingredients) };
    });
    setIsEditAiDraftPortionModalVisible(false);
    setEditingIngredient(null);
    setEditingIngredientIndex(null);
    setSelectedAiDraftMealIndex(null);
  };

  const handleDeleteAiDraftIngredient = (mealIndex, ingredientIndex) => {
    updateAiDraft(mealIndex, (draft) => {
      const ingredients = [...(draft.ingredients || [])];
      ingredients.splice(ingredientIndex, 1);
      return { ...draft, ingredients, nutrition: sumIngredientNutrition(ingredients) };
    });
  };

  const renderIngredientRow = (ingredient, idx, {
    mealIndex,
    accentColor = 'emerald',
    onReplace,
    onEdit,
    onDelete,
    dishType,
    altIndex,
  }) => {
    const ingCalories = Math.round(ingredient.calories || 0);
    const ingProtein = parseFloat((ingredient.protein || 0).toFixed(1));
    const ingCarbs = parseFloat((ingredient.carbs || 0).toFixed(1));
    const ingFat = parseFloat((ingredient.fat || 0).toFixed(1));
    const accentDot = accentColor === 'blue' ? 'bg-progress' : 'bg-forest-ondark';

    return (
      <div key={idx} className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-md p-3 group hover:bg-canvas dark:hover:bg-canvas-dark/40 transition-colors duration-120">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className={`w-1.5 h-1.5 ${accentDot} rounded-full flex-shrink-0`} aria-hidden />
            <button
              type="button"
              onClick={() => onReplace(mealIndex, idx, dishType, altIndex)}
              className={`font-medium text-left truncate hover:text-forest-ondark dark:hover:text-forest-ondark transition-colors duration-120 ${themeClasses.textPrimary} text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-forest rounded-sm`}
              title={language === 'hebrew' ? 'לחץ להחלפת מרכיב' : 'Click to replace ingredient'}
            >
              {ingredient.item || ingredient.name || 'Unknown item'}
            </button>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-3">
              <span className={`text-[13px] font-medium bg-canvas dark:bg-canvas-dark px-1.5 py-0.5 rounded-sm ${themeClasses.textPrimary} border border-line dark:border-line-dark tabular-nums`}>
                {ingredient.household_measure ? (
                  <>
                    {ingredient.household_measure} <span className="text-[11px] font-medium text-ink-muted">({parseFloat((ingredient['portionSI(gram)'] || 0).toFixed(1))}g)</span>
                  </>
                ) : (
                  ingredient['portionSI(gram)'] ? `${parseFloat((ingredient['portionSI(gram)'] || 0).toFixed(1))}g` : 'N/A'
                )}
              </span>
              <div className={`flex items-center gap-2 text-[11px] tabular-nums font-semibold`}>
                <span className={macroText}>{ingCalories} <span className="font-normal text-ink-muted text-[11px]">cal</span></span>
                <span className="ind-protein hidden sm:inline">{ingProtein} <span className="font-medium">p</span></span>
                <span className="ind-carbs hidden sm:inline">{ingCarbs} <span className="font-medium">c</span></span>
                <span className="ind-fat hidden sm:inline">{ingFat} <span className="font-medium">f</span></span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onEdit(mealIndex, idx, ingredient, dishType, altIndex)}
                className={`${themeClasses.btnGhost} !px-2`}
                title={t('page.edit')}
              >
                <Edit2 size={16} aria-hidden />
                <span className="hidden sm:inline">{t('page.edit')}</span>
              </button>
              <button
                type="button"
                onClick={() => onDelete(mealIndex, idx, dishType, altIndex)}
                className={`${themeClasses.btnDanger} !px-2`}
                title={t('page.delete')}
              >
                <Trash2 size={16} aria-hidden />
                <span className="hidden sm:inline">{t('page.delete')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAiDraftEditor = (mealIndex, meal, preview) => {
    if (!preview) return null;
    const targetNutrition = meal.main?.nutrition || meal.main || {};
    const macroChip = getMacroDiffChip(preview.nutrition, targetNutrition);

    return (
      <div className="mt-4 p-4 rounded-lg border border-forest/25 bg-moss dark:bg-moss-dark">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            {editingMealTitle?.mealIndex === mealIndex && editingMealTitle?.type === 'aiDraft' ? (
              <input
                type="text"
                className={themeClasses.inputField + ' !text-[15px] !font-semibold'}
                defaultValue={preview.meal_title || preview.meal_name || t('page.aiSuggestion')}
                autoFocus
                onBlur={(e) => handleAiDraftTitleChange(mealIndex, e.target.value.trim())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.target.blur();
                  else if (e.key === 'Escape') setEditingMealTitle(null);
                }}
              />
            ) : (
              <h5
                className={`${themeClasses.textPrimary} text-[15px] font-semibold cursor-pointer hover:text-forest-ondark transition-colors duration-120 inline-flex items-center gap-2`}
                onClick={() => setEditingMealTitle({ mealIndex, type: 'aiDraft' })}
                title={language === 'hebrew' ? 'לחץ לעריכת שם המנה' : 'Click to edit dish name'}
              >
                {preview.meal_title || preview.meal_name || t('page.aiSuggestion')}
                <Edit2 size={14} className="opacity-50" aria-hidden />
              </h5>
            )}
            {preview.main_protein_source && (
              <p className={`${themeClasses.textMuted} text-[11px] mt-1`}>
                {t('page.mainProtein')}: {preview.main_protein_source}
              </p>
            )}
          </div>
          <span className={`${pillSm} bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark`}>
            <Sparkles size={11} aria-hidden />
            {t('page.aiDraftBadge')}
          </span>
        </div>

        {preview.nutrition && (
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-2 text-[13px] font-semibold tabular-nums">
              <span className={macroText}>{Math.round(preview.nutrition.calories || 0)} <span className="font-normal text-ink-muted text-[11px] uppercase tracking-wider">kcal</span></span>
              <span className="w-1 h-1 rounded-full bg-line dark:bg-line-dark" aria-hidden />
              <span className="ind-protein">P: {roundMacro(preview.nutrition.protein)}g</span>
              <span className="w-1 h-1 rounded-full bg-line dark:bg-line-dark" aria-hidden />
              <span className="ind-carbs">C: {roundMacro(preview.nutrition.carbs)}g</span>
              <span className="w-1 h-1 rounded-full bg-line dark:bg-line-dark" aria-hidden />
              <span className="ind-fat">F: {roundMacro(preview.nutrition.fat)}g</span>
              {macroChip?.withinTarget && (
                <span
                  className={`${pillSm} bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark`}
                  title={macroChip.detail}
                >
                  <CheckCircle size={11} aria-hidden />
                  {macroChip.label} · {t('page.macroDiffFromTarget')}
                </span>
              )}
            </div>
            {macroChip && !macroChip.withinTarget && (
              <div
                className="mt-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-[13px]"
                title={macroChip.detail}
              >
                <div className="font-semibold text-amber-800 dark:text-amber-300 mb-1 text-[13px]">
                  {t('page.macroDiffWarn')} · {t('page.macroDiffFromTarget')}
                </div>
                <ul className="space-y-1 text-amber-800/90 dark:text-amber-200/90">
                  {macroChip.outOfRange.map((item) => (
                    <li key={item.key} className="flex items-start gap-1.5">
                      <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
                      <span>{item.line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="rounded-lg border border-line dark:border-line-dark p-3 bg-canvas dark:bg-canvas-dark">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h4 className={`${themeClasses.textPrimary} text-[13px] font-semibold`}>{t('page.ingredients')}</h4>
              {preview.ingredients?.length > 0 && (
                <span className={`${pillSm} bg-moss text-ink-muted dark:bg-moss-dark dark:text-ink-muted-dark`}>
                  {preview.ingredients.length} {t('page.items')}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleOpenAddAiDraftIngredient(mealIndex)}
              className={themeClasses.btnSecondary}
            >
              <Plus size={16} aria-hidden /> {t('page.addIngredient')}
            </button>
          </div>
          {preview.ingredients?.length > 0 ? (
            <div className="space-y-2">
              {preview.ingredients.map((ingredient, idx) =>
                renderIngredientRow(ingredient, idx, {
                  mealIndex,
                  accentColor: 'purple',
                  onReplace: handleReplaceAiDraftIngredient,
                  onEdit: handleEditAiDraftIngredient,
                  onDelete: handleDeleteAiDraftIngredient,
                })
              )}
            </div>
          ) : (
            <p className={`${themeClasses.textMuted} text-[13px] text-center py-4`}>{t('page.noIngredients')}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button
            type="button"
            onClick={() => handleApplyAiDraftToPlan(mealIndex)}
            disabled={!!isApplyingAiDraft[mealIndex]}
            aria-busy={!!isApplyingAiDraft[mealIndex]}
            className={themeClasses.btnPrimary}
          >
            {isApplyingAiDraft[mealIndex] ? (
              <RefreshCw size={16} className="animate-spin" aria-hidden />
            ) : (
              <CheckCircle size={16} aria-hidden />
            )}
            {isApplyingAiDraft[mealIndex] ? t('page.addToPlanSaving') : t('page.addToPlan')}
          </button>
          <button
            type="button"
            onClick={() => handleDiscardAiDraft(mealIndex)}
            className={themeClasses.btnSecondary}
          >
            <X size={16} aria-hidden /> {t('page.discardDraft')}
          </button>
        </div>
      </div>
    );
  };

  const handleMealTitleChange = (mealIndex, type, newTitle, altIndex = null) => {
    if (!selectedMealPlan?.meal_plan?.meals) return;
    const updatedPlanData = { ...selectedMealPlan };
    const updatedMeals = [...updatedPlanData.meal_plan.meals];
    let mealToUpdate = { ...updatedMeals[mealIndex] };
    if (type === 'main' && mealToUpdate.main) {
      mealToUpdate.main = { ...mealToUpdate.main, meal_title: newTitle || mealToUpdate.main.meal_title };
    } else if (type === 'alternative' && mealToUpdate.alternative) {
      mealToUpdate.alternative = { ...mealToUpdate.alternative, meal_title: newTitle || mealToUpdate.alternative.meal_title };
    } else if (type === 'alternatives' && altIndex != null && mealToUpdate.alternatives?.[altIndex]) {
      const alternatives = [...mealToUpdate.alternatives];
      alternatives[altIndex] = { ...alternatives[altIndex], meal_title: newTitle || alternatives[altIndex].meal_title };
      mealToUpdate.alternatives = alternatives;
    }
    updatedMeals[mealIndex] = mealToUpdate;
    updatedPlanData.meal_plan = { ...updatedPlanData.meal_plan, meals: updatedMeals };
    setSelectedMealPlan(updatedPlanData);
    setEditingMealTitle(null);
  };

  // Slots stay put; only the dish (main + alternatives) moves. The dish takes
  // on the target slot's name so saving and the client app see the right meal.
  const swapMealDishes = (fromIndex, toIndex) => {
    const list = selectedMealPlan?.meal_plan?.meals;
    if (!list || fromIndex === toIndex) return;
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) return;

    const slotName = (m) => m?.main?.meal_name ?? m?.meal;
    const renamed = (part, name) => (part ? { ...part, meal_name: name } : part);
    const dishFor = (dish, target) => {
      const name = slotName(target);
      return {
        ...target,
        main: renamed(dish.main, name),
        alternative: renamed(dish.alternative, name),
        alternatives: Array.isArray(dish.alternatives)
          ? dish.alternatives.map((alt) => renamed(alt, name))
          : dish.alternatives,
      };
    };

    const updatedMeals = [...list];
    updatedMeals[toIndex] = dishFor(list[fromIndex], list[toIndex]);
    updatedMeals[fromIndex] = dishFor(list[toIndex], list[fromIndex]);
    setSelectedMealPlan({
      ...selectedMealPlan,
      meal_plan: { ...selectedMealPlan.meal_plan, meals: updatedMeals },
    });

    // Open/closed state follows the dish.
    const swapAt = (prev) => ({ ...prev, [fromIndex]: prev[toIndex], [toIndex]: prev[fromIndex] });
    setExpandedMeals(swapAt);
    setExpandedAlternatives(swapAt);
    setEditingMealTitle(null);
  };

  const mealDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const [draggingMealIndex, setDraggingMealIndex] = useState(null);

  // --- SAVE MEAL PLAN ---
  const handleSaveMealPlan = async () => {
    if (!selectedMealPlan?.id) return;
    setIsSaving(true);
    try {
      const originalPlan = await fetchMealPlanById(selectedMealPlan.id);
      if (!originalPlan || !originalPlan.meal_plan || !originalPlan.meal_plan.meals) {
        throw new Error('Original meal plan not found or invalid');
      }
      if (!selectedMealPlan.meal_plan || !selectedMealPlan.meal_plan.meals) {
        throw new Error('Current meal plan data is invalid');
      }
      
      const mergeMealWithOriginal = (editedMeal, originalMeal) => {
        if (!originalMeal) return normalizeMealAlternativeSlots(editedMeal);
        const merged = {
          ...editedMeal,
          meal: originalMeal.meal,
          main: editedMeal.main ? {
            ...editedMeal.main,
            meal_title: editedMeal.main.meal_title ?? originalMeal.main?.meal_title ?? editedMeal.main.meal_name ?? editedMeal.main.title,
            meal_name: editedMeal.main.meal_name ?? originalMeal.main?.meal_name ?? editedMeal.main.title,
            title: editedMeal.main.title ?? originalMeal.main?.title ?? editedMeal.main.meal_title,
            ingredients: editedMeal.main.ingredients
          } : editedMeal.main,
          alternative: editedMeal.alternative ? {
            ...editedMeal.alternative,
            meal_title: editedMeal.alternative.meal_title ?? originalMeal.alternative?.meal_title ?? editedMeal.alternative.meal_name,
            meal_name: editedMeal.alternative.meal_name ?? originalMeal.alternative?.meal_name,
            ingredients: editedMeal.alternative.ingredients
          } : editedMeal.alternative,
          alternatives: editedMeal.alternatives ?? originalMeal.alternatives ?? [],
        };
        return normalizeMealAlternativeSlots(merged);
      };

      const originalMeals = originalPlan.meal_plan.meals;
      const usedOrigIndices = new Set();
      const mealsOrdered = selectedMealPlan.meal_plan.meals.map((editedMeal) => {
        let origIdx = -1;
        for (let i = 0; i < originalMeals.length; i++) {
          if (usedOrigIndices.has(i)) continue;
          if (originalMeals[i].meal === editedMeal.meal) {
            origIdx = i;
            break;
          }
        }
        if (origIdx === -1) {
          origIdx = originalMeals.findIndex((_, i) => !usedOrigIndices.has(i));
        }
        if (origIdx === -1) return editedMeal;
        usedOrigIndices.add(origIdx);
        return mergeMealWithOriginal(editedMeal, originalMeals[origIdx]);
      });

      const mealPlanToSave = {
        ...originalPlan.meal_plan,
        meals: mealsOrdered,
        totals: selectedMealPlan.meal_plan.totals
      };
      
      const calories = mealPlanToSave.totals?.calories;
      const dailyTotalCalories = calories ? Math.round(calories) : null;

      await updateMealPlan(selectedMealPlan.id, {
        meal_plan: mealPlanToSave,
        daily_total_calories: dailyTotalCalories,
      });

      const plans = await fetchClientMealPlans(selectedClient.user_code);
      setMealPlans(plans || []);
      const updatedPlan = await fetchMealPlanById(selectedMealPlan.id);
      
      if (language === 'hebrew' && updatedPlan.meal_plan && !isDemoMealPlanId(updatedPlan.id)) {
        try {
          const menuToTranslate = {
            meals: updatedPlan.meal_plan.meals || [],
            note: updatedPlan.meal_plan.note || ''
          };
          const translatedMenu = await translateMenu(menuToTranslate, 'he');
          if (translatedMenu && translatedMenu.meals && Array.isArray(translatedMenu.meals)) {
            updatedPlan.meal_plan = {
              ...updatedPlan.meal_plan,
              meals: translatedMenu.meals,
              note: translatedMenu.note || updatedPlan.meal_plan.note || ''
            };
          }
        } catch (translateError) {}
      }
      setSelectedMealPlan(updatedPlan);
      toast.success(language === 'hebrew' ? 'תוכנית הארוחות נשמרה בהצלחה' : 'Meal plan saved successfully');
    } catch (error) {
      toast.error(language === 'hebrew' ? 'שגיאה בשמירת תוכנית הארוחות' : 'Error saving meal plan');
    } finally {
      setIsSaving(false);
    }
  };

  // --- RENDER HELPERS ---
  const renderStatusPill = (status) => {
    // Never show a raw key: an unknown status falls back to readable words ("on_hold" → "On hold").
    const key = `page.status.${status}`;
    const translated = t(key);
    const words = String(status || '').replace(/[_-]+/g, ' ').trim();
    const label = translated !== key ? translated : words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
    let Icon = AlertCircle;
    let cls = 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
    if (status === 'active') {
      Icon = CheckCircle;
      cls = 'bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark';
    } else if (status === 'scheduled') {
      Icon = Calendar;
      cls = 'bg-progress-faint text-progress';
    } else if (status === 'expired') {
      Icon = PowerOff;
      cls = 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400';
    }
    return (
      <span className={`${pillSm} ${cls}`}>
        <Icon size={11} aria-hidden />
        {label}
      </span>
    );
  };

  const getMealIcon = (mealName) => {
    const name = (mealName || '').toLowerCase();
    if (name.includes('breakfast') || name.includes('בוקר')) return <Sunrise size={18} className="text-[color:var(--warning)]" aria-hidden />;
    if (name.includes('lunch') || name.includes('צהריים')) return <Sun size={18} className="text-forest-ondark" aria-hidden />;
    if (name.includes('dinner') || name.includes('ערב')) return <Moon size={18} className="text-progress" aria-hidden />;
    if (name.includes('snack') || name.includes('חטיף')) return <Apple size={18} className="ind-protein" aria-hidden />;
    return <Utensils size={18} className="text-ink-muted dark:text-ink-muted-dark" aria-hidden />;
  };

  const renderPlanCard = (plan, { featured = false, tourView = false } = {}) => {
    const title = language === 'english'
      ? `${t('page.mealPlanLabel')} · ${new Date(plan.created_at).toLocaleDateString()}`
      : plan.meal_plan_name;
    const isLive = plan.status === 'active' || plan.status === 'scheduled';
    return (
      <div
        key={plan.id}
        className={`${cardBase} p-4 flex flex-col ${
          featured ? 'border-forest/40 bg-forest-faint dark:bg-forest-faint-dark sm:p-5' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-4">
          <h3
            className={`${featured ? 'text-[18px]' : 'text-[15px]'} font-semibold ${themeClasses.textPrimary} truncate`}
            title={plan.meal_plan_name}
          >
            {title}
          </h3>
          {renderStatusPill(plan.status)}
        </div>

        <div className="space-y-1.5 text-[13px] mb-4 flex-1">
          <div className={`${themeClasses.textMuted} flex items-center justify-between`}>
            <span>{t('page.createdAt')}</span>
            <span className={`${themeClasses.textPrimary} tabular-nums`}>{new Date(plan.created_at).toLocaleDateString()}</span>
          </div>
          {plan.active_from && (
            <div className={`${themeClasses.textMuted} flex items-center justify-between`}>
              <span>{t('page.activeFrom')}</span>
              <span className={`${themeClasses.textPrimary} tabular-nums`}>{new Date(plan.active_from).toLocaleDateString()}</span>
            </div>
          )}
          {plan.active_until && (
            <div className={`${themeClasses.textMuted} flex items-center justify-between`}>
              <span>{t('page.activeUntil')}</span>
              <span className={`${themeClasses.textPrimary} tabular-nums`}>{new Date(plan.active_until).toLocaleDateString()}</span>
            </div>
          )}
          {plan.active_days && plan.active_days.length > 0 && (
            <div className={`${themeClasses.textMuted} flex items-center justify-between gap-2`}>
              <span className="shrink-0">{t('page.activeDays')}</span>
              <span className={`text-[11px] ${themeClasses.textPrimary} text-end`}>{plan.active_days.map(d => dayNames[d]).join(', ')}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 mt-auto">
          <button
            type="button"
            data-tour={tourView ? 'playbook-plan-view' : undefined}
            onClick={(e) => { e.stopPropagation(); handleViewMealPlan(plan.id); }}
            className={`${featured ? themeClasses.btnPrimary : themeClasses.btnSecondary} col-span-1`}
          >
            {t('page.view')}
          </button>
          {isLive ? (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleOpenEditScheduleModal(plan); }}
                disabled={isSaving}
                className={themeClasses.btnSecondary + ' col-span-1'}
              >
                <Calendar size={16} aria-hidden />
                <span className="truncate">{t('page.editSchedule')}</span>
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleDeactivate(plan); }}
                disabled={isSaving}
                className={themeClasses.btnDanger + ' col-span-1'}
              >
                <PowerOff size={16} aria-hidden />
                <span className="truncate">{t('page.deactivate')}</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleOpenActivateModal(plan); }}
              className={`${themeClasses.btnSecondary} col-span-1 bg-forest-faint text-forest-ondark border-forest/30 dark:bg-forest-faint-dark`}
            >
              <Power size={16} aria-hidden />
              <span className="truncate">{t('page.activate')}</span>
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDeletePlan(plan); }}
            disabled={isSaving}
            className={themeClasses.btnDanger + ' col-span-1'}
          >
            <Trash2 size={16} aria-hidden />
            <span className="truncate">{t('page.deletePlan')}</span>
          </button>
        </div>
      </div>
    );
  };

  const toggleMealExpand = (index) => {
    setExpandedMeals(prev => ({ ...prev, [index]: !prev[index] }));
  };

  if (!selectedClient) {
    return (
      <ClientWorkspaceLayout>
        <div />
      </ClientWorkspaceLayout>
    );
  }

  // --- LIST VIEW ---
  if (!selectedMealPlan) {
    const livePlans = mealPlans.filter((p) => p.status === 'active');
    const otherPlans = mealPlans.filter((p) => p.status !== 'active');
    return (
      <ClientWorkspaceLayout>
        <div className={`min-h-screen ${themeClasses.bgPrimary} font-sans`} dir={direction}>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h1 className={`font-display text-4xl font-medium tracking-tight ${themeClasses.textPrimary}`}>{t('page.title')}</h1>
            <button
              type="button"
              onClick={() => navigate('/create-meal-plan')}
              className={themeClasses.btnPrimary}
            >
              <Plus size={16} aria-hidden />
              {t('page.createPlan')}
            </button>
          </div>

          {isLoading ? (
            <div className={`text-center ${themeClasses.textMuted} py-12 flex flex-col items-center gap-3`}>
              <RefreshCw className="w-6 h-6 text-ink-muted animate-spin" aria-hidden />
              <span className="text-[13px]">{t('page.loading')}</span>
            </div>
          ) : mealPlans.length === 0 ? (
            <div className={`text-center ${themeClasses.textMuted} py-12 rounded-lg border border-dashed border-line dark:border-line-dark`}>
              <Utensils size={24} className="mx-auto mb-3 text-ink-muted" aria-hidden />
              <p className="text-[13px]">{t('page.noMealPlans')}</p>
            </div>
          ) : (
            <div data-tour="playbook-plan-list" className="space-y-8">
                    <section>
                      <h2 className={`text-[15px] font-semibold ${themeClasses.textPrimary}`}>{t('page.currentPlan')}</h2>
                      <p className={`${themeClasses.textMuted} text-[13px] mt-0.5 mb-3`}>{t('page.currentPlanHint')}</p>
                      {livePlans.length > 0 ? (
                        <div className="grid grid-cols-1 gap-4">
                          {livePlans.map((plan, i) => renderPlanCard(plan, { featured: true, tourView: i === 0 }))}
                        </div>
                      ) : (
                        <div className={`text-center py-8 px-4 rounded-lg border border-dashed border-line dark:border-line-dark ${isDarkMode ? 'bg-canvas-dark/40' : 'bg-canvas/90'}`}>
                          <PowerOff size={20} className="mx-auto mb-2 text-red-700 dark:text-red-400" aria-hidden />
                          <p className={`text-[13px] font-semibold ${themeClasses.textPrimary}`}>{t('page.noCurrentPlan')}</p>
                          <p className={`text-[13px] mt-1 ${themeClasses.textMuted}`}>{t('page.noCurrentPlanHint')}</p>
                        </div>
                      )}
                    </section>
                    {otherPlans.length > 0 && (
                      <section>
                        <h2 className={`text-[15px] font-semibold ${themeClasses.textPrimary}`}>{t('page.morePlans')}</h2>
                        <p className={`${themeClasses.textMuted} text-[13px] mt-0.5 mb-3`}>{t('page.morePlansHint')}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {otherPlans.map((plan, i) => renderPlanCard(plan, { tourView: livePlans.length === 0 && i === 0 }))}
                        </div>
                      </section>
                    )}
            </div>
          )}
        </main>

        {/* Activation Modal */}
        {isActivateModalVisible && planToActivate && (
          <div className={overlayClass} onClick={handleCloseActivateModal}>
            <div className={`${cardBase} p-4 sm:p-5 max-w-md w-full mx-4 overflow-y-auto max-h-[90vh]`} onClick={(e) => e.stopPropagation()} dir={direction}>
              <div className="mb-4 flex justify-between items-start gap-2">
                <div>
                  <h3 className={`${themeClasses.textPrimary} text-[18px] font-semibold mb-1`}>
                    {isEditScheduleMode ? t('page.activateModal.editTitle') : t('page.activateModal.title')}
                  </h3>
                  <p className={`${themeClasses.textMuted} text-[13px]`}>{planToActivate.meal_plan_name}</p>
                </div>
                <button type="button" onClick={handleCloseActivateModal} className={iconBtn} aria-label={t('page.activateModal.cancel')}>
                  <X size={16} aria-hidden />
                </button>
              </div>

              {activeDays.length === 0 ? (
                <div className={`mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 ${themeClasses.textPrimary} text-[13px]`} role="alert">
                  <p className="font-semibold text-amber-800 dark:text-amber-300">{t('page.activateModal.onePlanPerWholeWeek')}</p>
                  {conflictingPlansForActivation.length > 0 && (
                    <p className="mt-2 text-amber-700 dark:text-amber-400">
                      {t('page.activateModal.willDeactivateCurrent')}
                      <span className="block mt-1 font-bold">
                        {t('page.activateModal.willCancelCurrentPlans').replace('{{names}}', conflictingPlansForActivation.map((p) => p.meal_plan_name || t('page.mealPlanName')).join(', '))}
                      </span>
                    </p>
                  )}
                </div>
              ) : conflictingPlansForActivation.length > 0 ? (
                <div className={`mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 ${themeClasses.textPrimary} text-[13px]`} role="alert">
                  <p className="font-semibold text-amber-800 dark:text-amber-300">{t('page.activateModal.willCancelCurrent')}</p>
                  <p className="mt-1 text-amber-700 dark:text-amber-400">
                    {t('page.activateModal.willCancelCurrentPlans').replace('{{names}}', conflictingPlansForActivation.map((p) => p.meal_plan_name || t('page.mealPlanName')).join(', '))}
                  </p>
                </div>
              ) : activationImpactMessage ? (
                <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-[13px]" role="alert">
                  {activationImpactMessage}
                </div>
              ) : null}
              
              <div className="space-y-5">
                <div>
                  <label htmlFor="active-from" className={themeClasses.fieldLabel}>
                    {t('page.activateModal.activeFrom')}
                  </label>
                  <input
                    id="active-from"
                    type="date"
                    value={activeFrom}
                    onChange={(e) => setActiveFrom(e.target.value)}
                    className={themeClasses.inputField}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="active-until" className={themeClasses.fieldLabel}>
                    {t('page.activateModal.activeUntil')}
                  </label>
                  <input
                    id="active-until"
                    type="date"
                    value={activeUntil}
                    onChange={(e) => setActiveUntil(e.target.value)}
                    className={themeClasses.inputField}
                    required
                  />
                </div>

                <div>
                  <label className={themeClasses.fieldLabel}>
                    {t('page.activateModal.activeDays')} <span className="font-normal">({language === 'hebrew' ? 'אופציונלי' : 'Optional'})</span>
                  </label>
                  <p className={`${themeClasses.textMuted} text-xs mb-3`}>
                    {language === 'hebrew' 
                      ? 'בחר ימים ספציפיים או השאר ריק לכל הימים' 
                      : 'Select specific days or leave empty for all days'}
                  </p>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {dayNames.map((dayName, dayIndex) => (
                      <button
                        key={dayIndex}
                        type="button"
                        onClick={() => handleToggleDay(dayIndex)}
                        className={`min-h-11 px-2 py-2 rounded-md text-[13px] font-medium border transition-colors duration-120 outline-none focus-visible:ring-2 focus-visible:ring-forest ${
                          activeDays.includes(dayIndex)
                            ? 'bg-forest text-white border-forest'
                            : `${isDarkMode ? 'bg-surface-dark text-ink-muted-dark border-line-dark hover:bg-moss-dark' : 'bg-canvas text-ink-muted border-line hover:bg-moss'}`
                        }`}
                        title={dayName}
                      >
                        {dayName.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {!isEditScheduleMode && (
              <label className={`flex items-start gap-3 mt-5 p-3 rounded-md border ${isDarkMode ? 'border-line-dark bg-canvas-dark' : 'border-line bg-canvas'} cursor-pointer select-none transition-colors duration-120 hover:bg-moss dark:hover:bg-moss-dark`}>
                  <input
                    type="checkbox"
                    checked={sendNotifyAfterActivate}
                    onChange={(e) => setSendNotifyAfterActivate(e.target.checked)}
                    className="mt-1 rounded border-line text-forest focus:ring-forest shrink-0 w-4 h-4"
                  />
                  <span className="min-w-0">
                    <span className={`${themeClasses.textPrimary} text-sm font-semibold block`}>
                      {t('page.activateModal.notifyCheckbox')}
                    </span>
                    <span className={`${themeClasses.textMuted} text-xs block mt-1`}>
                      {t('page.activateModal.notifyCheckboxHint')}
                    </span>
                  </span>
                </label>
              )}

              <div className="flex gap-3 mt-8">
                <button onClick={handleCloseActivateModal} className={themeClasses.btnSecondary + ' flex-1'}>
                  {t('page.activateModal.cancel')}
                </button>
                <button
                  onClick={handleActivate}
                  disabled={isSaving || !activeFrom || !activeUntil}
                  className={themeClasses.btnPrimary + ' flex-1'}
                >
                  {isSaving
                    ? (isEditScheduleMode ? t('page.activateModal.saving') : t('page.activateModal.activating'))
                    : (isEditScheduleMode ? t('page.activateModal.save') : t('page.activateModal.activate'))}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </ClientWorkspaceLayout>
    );
  }

  // --- DETAIL VIEW ---
  if (isLoadingPlan) {
    return (
      <ClientWorkspaceLayout>
        <div className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center`} dir={direction}>
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 text-ink-muted animate-spin" aria-hidden />
            <span className={`${themeClasses.textMuted} text-[13px]`}>{t('page.loading')}</span>
          </div>
        </div>
      </ClientWorkspaceLayout>
    );
  }

  if (!selectedMealPlan) {
    return (
      <ClientWorkspaceLayout>
        <div className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center`} dir={direction}>
          <div className={`text-center ${themeClasses.textMuted} py-12`}>
            <AlertCircle size={24} className="mx-auto mb-3 opacity-50" aria-hidden />
            <p className="text-[13px]">{language === 'hebrew' ? 'תוכנית הארוחות לא נטענה' : 'Meal plan not loaded'}</p>
          </div>
        </div>
      </ClientWorkspaceLayout>
    );
  }

  const mealPlanData = selectedMealPlan.meal_plan;
  if (!mealPlanData || !mealPlanData.meals || !Array.isArray(mealPlanData.meals)) {
    return (
      <ClientWorkspaceLayout>
        <div className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center`} dir={direction}>
          <div className={`text-center ${themeClasses.textMuted} py-12`}>
            <AlertCircle size={24} className="mx-auto mb-3 opacity-50" aria-hidden />
            <p className="text-[13px]">{language === 'hebrew' ? 'תוכנית הארוחות לא מכילה נתונים' : 'Meal plan contains no data'}</p>
          </div>
        </div>
      </ClientWorkspaceLayout>
    );
  }

  const mainTotals = mealPlanData.meals.reduce(
    (acc, meal) => {
      if (meal.main && meal.main.nutrition) {
        acc.calories += meal.main.nutrition.calories || 0;
        acc.protein += meal.main.nutrition.protein || 0;
        acc.carbs += meal.main.nutrition.carbs || 0;
        acc.fat += meal.main.nutrition.fat || 0;
      }
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const totalMacroCalories = (mainTotals.protein * 4) + (mainTotals.carbs * 4) + (mainTotals.fat * 9);
  const proteinCalories = mainTotals.protein * 4;
  const carbsCalories = mainTotals.carbs * 4;
  const fatCalories = mainTotals.fat * 9;
  const proteinPercentage = totalMacroCalories > 0 ? Math.round((proteinCalories / totalMacroCalories) * 100) : 0;
  const carbsPercentage = totalMacroCalories > 0 ? Math.round((carbsCalories / totalMacroCalories) * 100) : 0;
  const fatPercentage = totalMacroCalories > 0 ? Math.round((fatCalories / totalMacroCalories) * 100) : 0;

  return (
    <ClientWorkspaceLayout>
      <div className={`min-h-screen ${themeClasses.bgPrimary} font-sans`} dir={direction}>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        
        {/* Header — primary action is Save Changes */}
        <div className="mb-8 flex flex-col lg:flex-row justify-between lg:items-end gap-4">
          <div>
            <h1 className={`font-display text-4xl font-medium tracking-tight ${themeClasses.textPrimary} mb-2 break-words`}>{selectedMealPlan.meal_plan_name}</h1>
              <div className={`flex items-center gap-3 ${themeClasses.textSecondary} text-[13px]`}>
                <span className="font-medium">{t('page.status')}</span>
                {renderStatusPill(selectedMealPlan.status)}
              </div>
          </div>
          <div data-tour="playbook-plan-actions" className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setSelectedMealPlan(null)} className={themeClasses.btnSecondary}>
              <ArrowLeft size={16} aria-hidden /> {t('page.back')}
            </button>
            {(selectedMealPlan.status === 'active' || selectedMealPlan.status === 'scheduled') && (
              <>
                <button type="button" onClick={() => handleOpenEditScheduleModal(selectedMealPlan)} disabled={isSaving} className={themeClasses.btnSecondary}>
                  <Calendar size={16} aria-hidden /> {t('page.editSchedule')}
                </button>
                <button type="button" onClick={() => handleDeactivate(selectedMealPlan)} disabled={isSaving} className={themeClasses.btnDanger}>
                  <PowerOff size={16} aria-hidden /> {t('page.deactivate')}
                </button>
              </>
            )}
            <button type="button" onClick={handleSaveMealPlan} disabled={isSaving} aria-busy={isSaving} className={themeClasses.btnPrimary}>
              {isSaving ? <RefreshCw size={16} className="animate-spin" aria-hidden /> : <Save size={16} aria-hidden />}
              {isSaving ? t('page.saving') : t('page.save')}
            </button>
            <button type="button" onClick={() => handleDeletePlan(selectedMealPlan)} disabled={isSaving} className={themeClasses.btnDanger}>
              <Trash2 size={16} aria-hidden /> {t('page.delete')}
            </button>
          </div>
        </div>

        {/* Daily Summary Section */}
        <div data-tour="playbook-plan-summary" className="mb-8 border-b border-line dark:border-line-dark pb-8">
          <div>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-4">
              <div>
                <h2 className={`text-[11px] font-medium uppercase tracking-wider ${themeClasses.textMuted} mb-1`}>{t('page.dailySummary')}</h2>
                <div className="flex items-baseline gap-2">
                  <span className={`text-4xl font-medium tabular-nums tracking-tight ${themeClasses.textPrimary}`}>
                    {Math.round(mainTotals.calories).toLocaleString()}
                  </span>
                  <span className={`text-[13px] ${themeClasses.textMuted}`}>kcal</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-6 sm:gap-8">
                <div className="flex flex-col">
                  <span className="text-[11px] font-medium uppercase tracking-wider ind-protein mb-0.5">{t('page.protein')}</span>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-[18px] font-semibold tabular-nums ${themeClasses.textPrimary}`}>{parseFloat(mainTotals.protein.toFixed(1))}</span>
                    <span className={`text-[13px] ${themeClasses.textMuted}`}>g</span>
                  </div>
                  <span className={`text-[11px] ${themeClasses.textMuted}`}>{proteinPercentage}%</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[11px] font-medium uppercase tracking-wider ind-carbs mb-0.5">{t('page.carbs')}</span>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-[18px] font-semibold tabular-nums ${themeClasses.textPrimary}`}>{parseFloat(mainTotals.carbs.toFixed(1))}</span>
                    <span className={`text-[13px] ${themeClasses.textMuted}`}>g</span>
                  </div>
                  <span className={`text-[11px] ${themeClasses.textMuted}`}>{carbsPercentage}%</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[11px] font-medium uppercase tracking-wider ind-fat mb-0.5">{t('page.fat')}</span>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-[18px] font-semibold tabular-nums ${themeClasses.textPrimary}`}>{parseFloat(mainTotals.fat.toFixed(1))}</span>
                    <span className={`text-[13px] ${themeClasses.textMuted}`}>g</span>
                  </div>
                  <span className={`text-[11px] ${themeClasses.textMuted}`}>{fatPercentage}%</span>
                </div>
              </div>
            </div>

            <h3 className={`${themeClasses.textMuted} text-[11px] font-medium uppercase tracking-wider mb-2`}>{t('page.macroDistribution')}</h3>
            <div className="flex h-2 rounded-sm overflow-hidden bg-canvas dark:bg-canvas-dark" role="img" aria-label={`${t('page.macroDistribution')}: ${proteinPercentage}% protein, ${carbsPercentage}% carbs, ${fatPercentage}% fat`}>
              <div className="ind-protein-fill transition-all duration-120" style={{ width: `${proteinPercentage}%` }} />
              <div className="ind-carbs-fill transition-all duration-120" style={{ width: `${carbsPercentage}%` }} />
              <div className="ind-fat-fill transition-all duration-120" style={{ width: `${fatPercentage}%` }} />
            </div>
          </div>
        </div>

        {/* Meals List */}
        <div data-tour="playbook-plan-meals">
          <div className="flex items-center gap-2 mb-4">
            <Utensils className="text-ink-muted" size={16} aria-hidden />
            <h3 className={`${themeClasses.textPrimary} text-[15px] font-semibold`}>{t('page.meals')}</h3>
          </div>
          
          <DndContext
            sensors={mealDragSensors}
            collisionDetection={pointerWithin}
            onDragStart={({ active }) => setDraggingMealIndex(active.data.current?.index ?? null)}
            onDragCancel={() => setDraggingMealIndex(null)}
            onDragEnd={({ active, over }) => {
              setDraggingMealIndex(null);
              const from = active.data.current?.index;
              const to = over?.data?.current?.index;
              if (from != null && to != null && from !== to) swapMealDishes(from, to);
            }}
          >
          <div className="space-y-3">
            {mealPlanData.meals.map((meal, index) => {
              const mealCalories = meal.main?.nutrition?.calories || 0;
              const mealProtein = meal.main?.nutrition?.protein || 0;
              const mealCarbs = meal.main?.nutrition?.carbs || 0;
              const mealFat = meal.main?.nutrition?.fat || 0;
              
              const mealProteinCalories = mealProtein * 4;
              const mealCarbsCalories = mealCarbs * 4;
              const mealFatCalories = mealFat * 9;
              const mealTotalMacroCalories = mealProteinCalories + mealCarbsCalories + mealFatCalories;
              
              const mealProteinPercent = mealTotalMacroCalories > 0 ? Math.round((mealProteinCalories / mealTotalMacroCalories) * 100) : 0;
              const mealCarbsPercent = mealTotalMacroCalories > 0 ? Math.round((mealCarbsCalories / mealTotalMacroCalories) * 100) : 0;
              const mealFatPercent = mealTotalMacroCalories > 0 ? Math.round((mealFatCalories / mealTotalMacroCalories) * 100) : 0;
              const isExpanded = expandedMeals[index] === true;

              const slotLabel = translateMealName(meal.meal);
              const isDragSource = draggingMealIndex === index;
              return (
                <MealSlotDrop key={index} index={index} label={slotLabel} dropHint={t('page.dropToSwap')}>
                {/* Fixed slot label: stays in place while dishes move between slots. */}
                <div className="flex items-center gap-2 px-1 pt-1 pb-2">
                  <span className="text-ink-muted">{getMealIcon(meal.meal)}</span>
                  <span className={`${themeClasses.textSecondary} text-[12px] font-semibold`}>{slotLabel}</span>
                </div>
                <div
                  data-tour={index === 0 ? 'playbook-plan-meal-card' : undefined}
                  className={`${cardBase} px-4 py-5 transition-opacity duration-120 ${isDragSource ? 'opacity-40' : ''}`}
                >
                  {!meal.main ? (
                    <p className={`${themeClasses.textMuted} text-[13px] text-center py-4`}>{t('page.emptySlot')}</p>
                  ) : null}
                  <div
                    data-tour={index === 0 ? 'playbook-plan-meal-move' : undefined}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      <MealDragHandle
                        index={index}
                        label={`${t('page.dragDish')}: ${meal.main?.meal_title || meal.main?.title || slotLabel}`}
                        disabled={!meal.main || mealPlanData.meals.length < 2}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          {editingMealTitle?.mealIndex === index && editingMealTitle?.type === 'main' ? (
                            <input
                              type="text"
                              className={themeClasses.inputField + ' !text-[15px] sm:!text-lg !font-bold !py-1 !px-2 w-full max-w-sm'}
                              defaultValue={meal.main?.meal_title || meal.main?.title || translateMealName(meal.meal)}
                              autoFocus
                              onBlur={(e) => handleMealTitleChange(index, 'main', e.target.value.trim())}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.target.blur();
                                else if (e.key === 'Escape') setEditingMealTitle(null);
                              }}
                            />
                          ) : (
                            <h4
                              className={`${themeClasses.textPrimary} text-[16px] sm:text-[18px] font-semibold truncate cursor-pointer hover:text-forest-ondark transition-colors duration-120`}
                              onClick={() => { setEditingMealTitle({ mealIndex: index, type: 'main' }); }}
                              title={language === 'hebrew' ? 'לחץ לעריכת כותרת המנה' : 'Click to edit dish title'}
                            >
                              {meal.main?.meal_title || meal.main?.title || translateMealName(meal.meal)} <Edit2 size={16} className="inline ml-1 opacity-50" />
                            </h4>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 self-start sm:self-center">
                      {meal.main && mealPlanData.meals.length > 1 ? (
                        <select
                          value=""
                          onChange={(e) => {
                            const to = Number(e.target.value);
                            if (!Number.isNaN(to)) swapMealDishes(index, to);
                          }}
                          aria-label={t('page.moveToLabel')}
                          className="min-h-11 me-2 rounded-md border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark px-2 text-[13px] font-medium text-ink dark:text-ink-dark outline-none focus-visible:ring-2 focus-visible:ring-forest cursor-pointer"
                        >
                          <option value="" disabled>{t('page.moveTo')}</option>
                          {mealPlanData.meals.map((other, otherIndex) =>
                            otherIndex === index ? null : (
                              <option key={otherIndex} value={otherIndex}>
                                {translateMealName(other.meal)}
                              </option>
                            ),
                          )}
                        </select>
                      ) : null}
                      <button 
                        type="button"
                        data-tour={index === 0 ? 'playbook-plan-meal-expand' : undefined}
                        aria-expanded={isExpanded}
                        onClick={() => toggleMealExpand(index)}
                        className={`min-h-11 min-w-11 rounded-md flex items-center justify-center transition-colors duration-120 outline-none focus-visible:ring-2 focus-visible:ring-forest ${isExpanded ? 'bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark' : 'bg-canvas text-ink-muted dark:bg-canvas-dark dark:text-ink-muted-dark hover:bg-moss dark:hover:bg-moss-dark'}`}
                      >
                        <ChevronDown size={20} className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-[13px] font-semibold tabular-nums mb-3">
                    <span className={macroText}>{mealCalories.toLocaleString()} <span className="font-normal text-ink-muted text-[11px] uppercase tracking-wider">kcal</span></span>
                    <span className="w-1 h-1 rounded-full bg-line dark:bg-line-dark" aria-hidden />
                    <span className="ind-protein">P: {mealProtein}g</span>
                    <span className="w-1 h-1 rounded-full bg-line dark:bg-line-dark" aria-hidden />
                    <span className="ind-carbs">C: {mealCarbs}g</span>
                    <span className="w-1 h-1 rounded-full bg-line dark:bg-line-dark" aria-hidden />
                    <span className="ind-fat">F: {mealFat}g</span>
                  </div>

                  <div
                    data-tour={index === 0 ? 'playbook-plan-meal-expanded' : undefined}
                    className={`transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[5000px] opacity-100 mt-6' : 'max-h-0 opacity-0'} overflow-hidden`}
                  >
                    
                    {/* Meal Macro Bars */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                      <div className="flex flex-col gap-1">
                        <div className="flex justify-between text-[11px] font-medium">
                          <span className="ind-protein">{t('page.protein')}</span>
                          <span className={`${themeClasses.textPrimary} tabular-nums`}>{mealProteinPercent}%</span>
                        </div>
                        <div className="w-full bg-canvas dark:bg-canvas-dark rounded-sm h-1.5">
                          <div className="ind-protein-fill h-1.5 rounded-sm transition-all duration-120" style={{ width: `${mealProteinPercent}%` }} />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="flex justify-between text-[11px] font-medium">
                          <span className="ind-carbs">{t('page.carbs')}</span>
                          <span className={`${themeClasses.textPrimary} tabular-nums`}>{mealCarbsPercent}%</span>
                        </div>
                        <div className="w-full bg-canvas dark:bg-canvas-dark rounded-sm h-1.5">
                          <div className="ind-carbs-fill h-1.5 rounded-sm transition-all duration-120" style={{ width: `${mealCarbsPercent}%` }} />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="flex justify-between text-[11px] font-medium">
                          <span className="ind-fat">{t('page.fat')}</span>
                          <span className={`${themeClasses.textPrimary} tabular-nums`}>{mealFatPercent}%</span>
                        </div>
                        <div className="w-full bg-canvas dark:bg-canvas-dark rounded-sm h-1.5">
                          <div className="ind-fat-fill h-1.5 rounded-sm transition-all duration-120" style={{ width: `${mealFatPercent}%` }} />
                        </div>
                      </div>
                    </div>

                    {/* Ingredients List */}
                    <div
                      data-tour={index === 0 ? 'playbook-plan-meal-ingredients' : undefined}
                      className={`rounded-lg p-4 border border-line dark:border-line-dark ${isDarkMode ? 'bg-surface-dark' : 'bg-canvas'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className={`${themeClasses.textPrimary} text-[15px] font-semibold`}>{t('page.ingredients')}</h4>
                          {meal.main?.ingredients && (
                            <span className={`${pillSm} bg-moss text-ink-muted dark:bg-moss-dark dark:text-ink-muted-dark`}>
                              {meal.main.ingredients.length} {t('page.items')}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpenAddIngredient(index)}
                          className={themeClasses.btnSecondary}
                          title={language === 'hebrew' ? 'הוסף מרכיב' : 'Add ingredient'}
                        >
                          <Plus size={16} aria-hidden /> <span className="hidden sm:inline">{t('page.addIngredient')}</span>
                        </button>
                      </div>
                      
                      {meal.main?.ingredients && meal.main.ingredients.length > 0 ? (
                        <div className="space-y-3">
                          {meal.main.ingredients.map((ingredient, idx) => {
                            const ingCalories = Math.round(ingredient.calories || 0);
                            const ingProtein = parseFloat((ingredient.protein || 0).toFixed(1));
                            const ingCarbs = parseFloat((ingredient.carbs || 0).toFixed(1));
                            const ingFat = parseFloat((ingredient.fat || 0).toFixed(1));
                            
                            return (
                              <div key={idx} className={`${isDarkMode ? 'bg-surface-dark border-line-dark' : 'bg-surface border-line'} rounded-md p-3 border group hover:bg-canvas dark:hover:bg-canvas-dark/40 transition-colors duration-120`}>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <div className="w-1.5 h-1.5 bg-forest-ondark rounded-full flex-shrink-0" aria-hidden />
                                    <button
                                      type="button"
                                      onClick={() => handleReplaceIngredient(index, idx)}
                                      className={`font-medium text-left truncate hover:text-forest-ondark transition-colors duration-120 ${themeClasses.textPrimary} text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-forest rounded-sm`}
                                      title={language === 'hebrew' ? 'לחץ להחלפת מרכיב' : 'Click to replace ingredient'}
                                    >
                                      {ingredient.item || ingredient.name || 'Unknown item'}
                                    </button>
                                  </div>

                                  <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
                                    <div className="flex items-center gap-3">
                                      <span className={`text-[13px] font-medium bg-canvas dark:bg-canvas-dark px-1.5 py-0.5 rounded-sm ${themeClasses.textPrimary} border border-line dark:border-line-dark tabular-nums`}>
                                        {ingredient.household_measure ? (
                                            <>
                                            {ingredient.household_measure} <span className="text-[11px] font-medium text-ink-muted">({parseFloat((ingredient['portionSI(gram)'] || 0).toFixed(1))}g)</span>
                                            </>
                                        ) : (
                                            ingredient['portionSI(gram)'] ? `${parseFloat((ingredient['portionSI(gram)'] || 0).toFixed(1))}g` : 'N/A'
                                        )}
                                      </span>
                                      <div className="flex items-center gap-2 text-[11px] tabular-nums font-semibold">
                                          <span className={macroText}>{ingCalories} <span className="font-normal text-ink-muted text-[11px]">cal</span></span>
                                          <span className="ind-protein hidden sm:inline">{ingProtein} p</span>
                                          <span className="ind-carbs hidden sm:inline">{ingCarbs} c</span>
                                          <span className="ind-fat hidden sm:inline">{ingFat} f</span>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => handleEditIngredient(index, idx, ingredient)}
                                        className={`${themeClasses.btnGhost} !px-2`}
                                        title={t('page.edit')}
                                      >
                                        <Edit2 size={16} aria-hidden />
                                        <span className="hidden sm:inline">{t('page.edit')}</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteIngredient(index, idx)}
                                        className={`${themeClasses.btnDanger} !px-2`}
                                        title={t('page.delete')}
                                      >
                                        <Trash2 size={16} aria-hidden />
                                        <span className="hidden sm:inline">{t('page.delete')}</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-6 border border-dashed border-line dark:border-line-dark rounded-lg">
                          <p className={`${themeClasses.textMuted} text-sm font-medium`}>{t('page.noIngredients')}</p>
                        </div>
                      )}
                    </div>

                    {/* Alternative Meal Section */}
                    {(meal.alternative || (meal.alternatives && meal.alternatives.length > 0)) && (
                      <div
                        ref={(el) => { mealAlternativesRefs.current[index] = el; }}
                        className="mt-6 pt-6 border-t border-line dark:border-line-dark"
                      >
                        <button
                          type="button"
                          data-tour={index === 0 ? 'playbook-plan-meal-alts' : undefined}
                          aria-expanded={!!expandedAlternatives[index]}
                          onClick={() => setExpandedAlternatives(prev => ({...prev, [index]: !prev[index]}))}
                          className="w-full flex items-center justify-between rounded-lg p-3 transition-colors duration-120 border bg-progress-faint border-progress/30 hover:bg-progress-faint outline-none focus-visible:ring-2 focus-visible:ring-forest"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 border border-progress/30 bg-surface dark:bg-surface-dark text-progress rounded-md flex items-center justify-center">
                              <RefreshCw size={16} className={`transition-transform duration-200 ${expandedAlternatives[index] ? 'rotate-180' : ''}`} aria-hidden />
                            </div>
                            <div className="text-start">
                              <span className={`${themeClasses.textPrimary} text-[15px] font-semibold`}>
                                {meal.alternative && !meal.alternatives?.length ? t('page.alternativeMeal') : t('page.alternativeMeals')}
                              </span>
                              <p className={`${themeClasses.textMuted} text-[11px] mt-0.5`}>{t('page.clickToView')}</p>
                            </div>
                          </div>
                          <span
                            data-tour={index === 0 ? 'playbook-plan-meal-alts-chevron' : undefined}
                            className={`${themeClasses.textMuted} inline-flex items-center justify-center w-9 h-9 rounded-lg transition-transform duration-300 ${expandedAlternatives[index] ? 'rotate-180' : ''}`}
                          >
                            <ChevronDown size={20} />
                          </span>
                        </button>
                      </div>
                    )}

                    {expandedAlternatives[index] && (meal.alternative || (meal.alternatives && meal.alternatives.length > 0)) && (() => {
                      const alternativeDishes = [
                        ...(meal.alternative ? [{ dish: meal.alternative, dishType: 'alternative', altIndex: null }] : []),
                        ...(Array.isArray(meal.alternatives) ? meal.alternatives.map((alt, altIdx) => ({ dish: alt, dishType: 'alternatives', altIndex: altIdx })) : [])
                      ];
                      if (alternativeDishes.length === 0) return null;

                      return (
                        <div className="mt-4 space-y-4">
                          {alternativeDishes.map(({ dish, dishType, altIndex }, dishIdx) => (
                            <div key={dishIdx} className="p-4 rounded-lg border border-progress/25 bg-progress-faint">
                              <div className="mb-4 flex flex-wrap items-center gap-2 justify-between">
                                <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                                  {editingMealTitle?.mealIndex === index && editingMealTitle?.type === dishType && (editingMealTitle?.altIndex ?? null) === (altIndex ?? null) ? (
                                    <input
                                      type="text"
                                      className={themeClasses.inputField + ' !text-[15px] !font-semibold flex-1 min-w-[200px]'}
                                      defaultValue={dish.meal_title || dish.meal_name || translateMealName(meal.meal)}
                                      autoFocus
                                      onBlur={(e) => handleMealTitleChange(index, dishType, e.target.value.trim(), altIndex)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                        else if (e.key === 'Escape') setEditingMealTitle(null);
                                      }}
                                    />
                                  ) : (
                                    <h5
                                      className={`${themeClasses.textPrimary} text-[15px] font-semibold cursor-pointer hover:text-forest-ondark transition-colors duration-120 inline-flex items-center gap-2`}
                                      onClick={() => setEditingMealTitle({ mealIndex: index, type: dishType, altIndex })}
                                      title={language === 'hebrew' ? 'לחץ לעריכת שם המנה' : 'Click to edit dish name'}
                                    >
                                      {dish.meal_title || dish.meal_name || translateMealName(meal.meal)}
                                      <Edit2 size={14} className="opacity-50" />
                                    </h5>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAlternativeDish(index, dishType, altIndex)}
                                  disabled={countAlternativeDishes(meal) <= 1}
                                  className={`${themeClasses.btnDanger} shrink-0`}
                                  title={countAlternativeDishes(meal) <= 1 ? t('page.cannotDeleteLastAlternative') : t('page.deleteAlternative')}
                                >
                                  <Trash2 size={16} aria-hidden />
                                  <span className="hidden sm:inline">{t('page.delete')}</span>
                                </button>
                              </div>

                              <div className={`rounded-lg p-3 border border-line dark:border-line-dark ${isDarkMode ? 'bg-canvas-dark' : 'bg-canvas'}`}>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <h4 className={`${themeClasses.textPrimary} text-[13px] font-semibold`}>{t('page.ingredients')}</h4>
                                    {dish.ingredients && (
                                      <span className={`${pillSm} bg-moss text-ink-muted dark:bg-moss-dark dark:text-ink-muted-dark`}>
                                        {dish.ingredients.length} {t('page.items')}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleOpenAddAlternativeIngredient(index, dishType, altIndex)}
                                    className={themeClasses.btnSecondary}
                                    title={language === 'hebrew' ? 'הוסף מרכיב' : 'Add ingredient'}
                                  >
                                    <Plus size={16} aria-hidden /> <span className="hidden sm:inline">{t('page.addIngredient')}</span>
                                  </button>
                                </div>

                                {dish.ingredients && dish.ingredients.length > 0 ? (
                                  <div className="space-y-3">
                                    {dish.ingredients.map((ingredient, idx) =>
                                      renderIngredientRow(ingredient, idx, {
                                        mealIndex: index,
                                        accentColor: 'blue',
                                        dishType,
                                        altIndex,
                                        onReplace: handleReplaceAlternativeIngredient,
                                        onEdit: handleEditAlternativeIngredient,
                                        onDelete: handleDeleteAlternativeIngredient,
                                      })
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center py-6 border border-dashed border-line dark:border-line-dark rounded-lg">
                                    <p className={`${themeClasses.textMuted} text-sm font-medium`}>{t('page.noIngredients')}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* AI alternative draft workspace */}
                    <div className="mt-5 pt-5 rounded-lg p-4 bg-moss dark:bg-moss-dark border border-forest/25">
                      <div className="mb-3">
                        <h5 className={`${themeClasses.textPrimary} text-[15px] font-semibold flex items-center gap-2`}>
                          <Sparkles size={16} className="text-forest-ondark" aria-hidden />
                          {t('page.discoverAIAlternatives')}
                        </h5>
                        <p className={`${themeClasses.textMuted} text-[13px] mt-1`}>
                          {t('page.draftHelper')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDiscoverAlternative(index, meal)}
                        disabled={isDiscoverLoading[index]}
                        aria-busy={!!isDiscoverLoading[index]}
                        className={themeClasses.btnPrimary}
                      >
                        {isDiscoverLoading[index] ? (
                          <>
                            <RefreshCw size={16} className="animate-spin" aria-hidden />
                            {t('page.generatingSuggestion')}
                          </>
                        ) : aiDiscoverPreviews[index] ? (
                          t('page.generateAnother')
                        ) : (
                          t('page.generateSuggestion')
                        )}
                      </button>
                      {renderAiDraftEditor(index, meal, aiDiscoverPreviews[index])}
                    </div>
                  </div>
                </div>
                </MealSlotDrop>
              );
            })}
          </div>
          <DragOverlay dropAnimation={null}>
            {draggingMealIndex != null && mealPlanData.meals[draggingMealIndex] ? (() => {
              const dragged = mealPlanData.meals[draggingMealIndex];
              return (
                <div className="flex items-center gap-3 rounded-lg border border-forest bg-surface dark:bg-surface-dark px-3 py-2.5 shadow-lg max-w-md cursor-grabbing">
                  <GripVertical size={18} className="text-ink-muted" aria-hidden />
                  <div className="w-9 h-9 border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark rounded-md flex items-center justify-center text-ink-muted flex-shrink-0">
                    {getMealIcon(dragged.meal)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold text-ink dark:text-ink-dark truncate">
                      {dragged.main?.meal_title || dragged.main?.title || translateMealName(dragged.meal)}
                    </p>
                    <p className="text-[12px] text-ink-muted tabular-nums">
                      {(dragged.main?.nutrition?.calories || 0).toLocaleString()} kcal
                    </p>
                  </div>
                </div>
              );
            })() : null}
          </DragOverlay>
          </DndContext>
        </div>
      </main>

      {/* Activate/Schedule Modal */}
      {isActivateModalVisible && planToActivate && (
        <div className={overlayClass} onClick={handleCloseActivateModal}>
          <div className={`${cardBase} p-4 sm:p-5 max-w-md w-full mx-4 overflow-y-auto max-h-[90vh]`} onClick={(e) => e.stopPropagation()} dir={direction}>
            <div className="mb-4 flex justify-between items-start gap-2">
              <div>
                <h3 className={`${themeClasses.textPrimary} text-[18px] font-semibold mb-1`}>
                  {isEditScheduleMode ? t('page.activateModal.editTitle') : t('page.activateModal.title')}
                </h3>
                <p className={`${themeClasses.textMuted} text-[13px]`}>{planToActivate.meal_plan_name}</p>
              </div>
              <button type="button" onClick={handleCloseActivateModal} className={iconBtn} aria-label={t('page.activateModal.cancel')}>
                <X size={16} aria-hidden />
              </button>
            </div>

            {activeDays.length === 0 ? (
              <div className={`mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 ${themeClasses.textPrimary} text-[13px]`} role="alert">
                <p className="font-semibold text-amber-800 dark:text-amber-300">{t('page.activateModal.onePlanPerWholeWeek')}</p>
                {conflictingPlansForActivation.length > 0 && (
                  <p className="mt-2 text-amber-700 dark:text-amber-400">
                    {t('page.activateModal.willDeactivateCurrent')}
                    <span className="block mt-1 font-semibold">
                      {t('page.activateModal.willCancelCurrentPlans').replace('{{names}}', conflictingPlansForActivation.map((p) => p.meal_plan_name || t('page.mealPlanName')).join(', '))}
                    </span>
                  </p>
                )}
              </div>
            ) : conflictingPlansForActivation.length > 0 ? (
              <div className={`mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 ${themeClasses.textPrimary} text-[13px]`} role="alert">
                <p className="font-semibold text-amber-800 dark:text-amber-300">{t('page.activateModal.willCancelCurrent')}</p>
                <p className="mt-1 text-amber-700 dark:text-amber-400">
                  {t('page.activateModal.willCancelCurrentPlans').replace('{{names}}', conflictingPlansForActivation.map((p) => p.meal_plan_name || t('page.mealPlanName')).join(', '))}
                </p>
              </div>
            ) : activationImpactMessage ? (
              <div className="mb-4 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-[13px]" role="alert">
                {activationImpactMessage}
              </div>
            ) : null}
            
            <div className="space-y-4">
              <div>
                <label htmlFor="detail-active-from" className={themeClasses.fieldLabel}>
                  {t('page.activateModal.activeFrom')}
                </label>
                <input
                  id="detail-active-from"
                  type="date"
                  value={activeFrom}
                  onChange={(e) => setActiveFrom(e.target.value)}
                  className={themeClasses.inputField}
                  required
                />
              </div>

              <div>
                <label htmlFor="detail-active-until" className={themeClasses.fieldLabel}>
                  {t('page.activateModal.activeUntil')}
                </label>
                <input
                  id="detail-active-until"
                  type="date"
                  value={activeUntil}
                  onChange={(e) => setActiveUntil(e.target.value)}
                  className={themeClasses.inputField}
                  required
                />
              </div>

              <div>
                <label className={themeClasses.fieldLabel}>
                  {t('page.activateModal.activeDays')} <span className="font-normal">({language === 'hebrew' ? 'אופציונלי' : 'Optional'})</span>
                </label>
                <p className={`${themeClasses.textMuted} text-[11px] mb-2`}>
                  {language === 'hebrew' 
                    ? 'בחר ימים ספציפיים או השאר ריק לכל הימים' 
                    : 'Select specific days or leave empty for all days'}
                </p>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {dayNames.map((dayName, dayIndex) => (
                    <button
                      key={dayIndex}
                      type="button"
                      onClick={() => handleToggleDay(dayIndex)}
                      className={`min-h-11 px-2 py-2 rounded-md text-[13px] font-medium border transition-colors duration-120 outline-none focus-visible:ring-2 focus-visible:ring-forest ${
                        activeDays.includes(dayIndex)
                          ? 'bg-forest text-white border-forest'
                          : `${isDarkMode ? 'bg-surface-dark text-ink-muted-dark border-line-dark hover:bg-moss-dark' : 'bg-canvas text-ink-muted border-line hover:bg-moss'}`
                      }`}
                      title={dayName}
                    >
                      {dayName.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {!isEditScheduleMode && (
              <label className={`flex items-start gap-3 mt-5 p-3 rounded-md border ${isDarkMode ? 'border-line-dark bg-canvas-dark' : 'border-line bg-canvas'} cursor-pointer select-none transition-colors duration-120 hover:bg-moss dark:hover:bg-moss-dark`}>
                <input
                  type="checkbox"
                  checked={sendNotifyAfterActivate}
                  onChange={(e) => setSendNotifyAfterActivate(e.target.checked)}
                  className="mt-1 rounded border-line text-forest focus:ring-forest shrink-0 w-4 h-4"
                />
                <span className="min-w-0">
                  <span className={`${themeClasses.textPrimary} text-[13px] font-medium block`}>
                    {t('page.activateModal.notifyCheckbox')}
                  </span>
                  <span className={`${themeClasses.textMuted} text-[11px] block mt-1`}>
                    {t('page.activateModal.notifyCheckboxHint')}
                  </span>
                </span>
              </label>
            )}

            <div className="flex gap-2 mt-5">
              <button type="button" onClick={handleCloseActivateModal} className={themeClasses.btnSecondary + ' flex-1'}>
                {t('page.activateModal.cancel')}
              </button>
              <button
                type="button"
                onClick={handleActivate}
                disabled={isSaving || !activeFrom || !activeUntil}
                aria-busy={isSaving}
                className={themeClasses.btnPrimary + ' flex-1'}
              >
                {isSaving
                  ? (isEditScheduleMode ? t('page.activateModal.saving') : t('page.activateModal.activating'))
                  : (isEditScheduleMode ? t('page.activateModal.save') : t('page.activateModal.activate'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Ingredient Modal */}
      <AddIngredientModal
        visible={isAddIngredientModalVisible}
        onClose={() => {
          setIsAddIngredientModalVisible(false);
          setSelectedMealIndex(null);
          setIsReplacingIngredient(false);
          setReplacingIngredientIndex(null);
        }}
        onAddIngredient={handleAddIngredient}
        mealName={selectedMealIndex !== null && selectedMealPlan?.meal_plan?.meals[selectedMealIndex]?.meal}
        clientRegion={selectedClient?.region || 'israel'}
        userCode={selectedClient?.user_code || selectedClient?.id}
        language={language}
      />

      {/* Edit Ingredient Portion Modal */}
      <IngredientPortionModal
        visible={isEditPortionModalVisible}
        onClose={() => {
          setIsEditPortionModalVisible(false);
          setEditingIngredient(null);
          setEditingIngredientIndex(null);
        }}
        onConfirm={handleUpdateIngredientPortion}
        ingredient={editingIngredient}
        clientRegion={selectedClient?.region || 'israel'}
        language={language}
      />

      {/* Add Alternative Ingredient Modal */}
      <AddIngredientModal
        visible={isAddAlternativeIngredientModalVisible}
        onClose={() => {
          setIsAddAlternativeIngredientModalVisible(false);
          setSelectedAlternativeMealIndex(null);
          setSelectedAlternativeType(null);
          setSelectedAlternativeIndex(null);
          setIsReplacingIngredient(false);
          setReplacingIngredientIndex(null);
        }}
        onAddIngredient={handleAddAlternativeIngredient}
        mealName={selectedAlternativeMealIndex !== null && selectedMealPlan?.meal_plan?.meals[selectedAlternativeMealIndex]?.meal}
        clientRegion={selectedClient?.region || 'israel'}
        userCode={selectedClient?.user_code || selectedClient?.id}
        language={language}
      />

      {/* Edit Alternative Ingredient Portion Modal */}
      <IngredientPortionModal
        visible={isEditAlternativePortionModalVisible}
        onClose={() => {
          setIsEditAlternativePortionModalVisible(false);
          setEditingIngredient(null);
          setEditingIngredientIndex(null);
          setSelectedAlternativeMealIndex(null);
          setSelectedAlternativeType(null);
          setSelectedAlternativeIndex(null);
        }}
        onConfirm={handleUpdateAlternativeIngredientPortion}
        ingredient={editingIngredient}
        clientRegion={selectedClient?.region || 'israel'}
        language={language}
      />

      {/* AI Draft Ingredient Modals */}
      <AddIngredientModal
        visible={isAddAiDraftIngredientModalVisible}
        onClose={() => {
          setIsAddAiDraftIngredientModalVisible(false);
          setSelectedAiDraftMealIndex(null);
          setIsReplacingIngredient(false);
          setReplacingIngredientIndex(null);
        }}
        onAddIngredient={handleAddAiDraftIngredient}
        mealName={selectedAiDraftMealIndex !== null && selectedMealPlan?.meal_plan?.meals[selectedAiDraftMealIndex]?.meal}
        clientRegion={selectedClient?.region || 'israel'}
        userCode={selectedClient?.user_code || selectedClient?.id}
        language={language}
      />

      <IngredientPortionModal
        visible={isEditAiDraftPortionModalVisible}
        onClose={() => {
          setIsEditAiDraftPortionModalVisible(false);
          setEditingIngredient(null);
          setEditingIngredientIndex(null);
          setSelectedAiDraftMealIndex(null);
        }}
        onConfirm={handleUpdateAiDraftIngredientPortion}
        ingredient={editingIngredient}
        clientRegion={selectedClient?.region || 'israel'}
        language={language}
      />
    </div>
  </ClientWorkspaceLayout>
  );
}

export default ClientMealPlansPage;